/**
 * Construtor de páginas.
 *
 * Recebe a configuração escolhida pelo usuário e devolve o miolo completo,
 * página por página, pronto para ser renderizado em HTML de impressão.
 *
 * Regra de ouro: nada aqui depende de medição em tempo de execução. Todas as
 * alturas são calculadas em milímetros a partir do papel escolhido, então o
 * resultado impresso é idêntico em qualquer navegador.
 */
import { construirPaleta } from '../color/theory.js';
import { misturar } from '../color/convert.js';
import * as D from '../calendar/datas.js';
import { feriadoEm, feriadosDoMes } from '../calendar/feriados.js';
import { DEVOCIONAIS } from '../content/devocional.js';
import { CURIOSIDADES } from '../content/curiosidades.js';
import { PERGUNTAS } from '../content/perguntas.js';
import { DESAFIOS } from '../content/estudar.js';
import { moteDoMes } from '../content/motes.js';
import { MOTIVACIONAIS } from '../content/motivacional.js';
import { caixa, campos, checklist, coluna, espaco, grupo, horarios, imagem, linhas, miniCalendario, rotulo, secao, separador, tabela, texto, } from './blocos.js';
import { PAPEIS, ENTRELINHA_PADRAO, medirPaginaDoMiolo, tamanhoFontePt } from './papel.js';
import { camadasDaPagina, camadasDosFundos } from './imagem.js';
import { enfeitesDePauta } from './motivos.js';
import { horasDoDia } from './horarios.js';
import { alturaDoTextoMm, linhasDoTexto } from './medidas.js';
import { ALTURA_CABECALHO_PAGINA_MM, ALTURA_RODAPE_MM, construirPaginasDoCaderno, } from './cadernos.js';
import { FATOR_LARGURA_FONTE, ROTULO_QUADRO, ROTULO_DIA_LIVRE, GRUPO_QUADRO, FORMATOS, MASCARAS_DADOS, ACABAMENTO_DO_ESTILO_VISUAL, ICONE_QUADRO, ROTULO_ESTILO, anosDaAgenda, quadrosLigados, ROTULO_CAIXA_MOVEL, CAIXAS_MOVEIS } from '../types/planner.js';
/* ------------------------------------------------------------------ */
/* Constantes de composição                                            */
/* ------------------------------------------------------------------ */
export const GAP_DIAS_MM = 5;
export const MAX_PAGINAS_DIA = 800;
/** Altura da faixa de quadros rápidos no pé da página de dia. */
export const ALTURA_FAIXA_MM = 21;
/**
 * Espaço vertical que a faixa ocupa.
 *
 * É a altura dela e mais nada: as seções da página de dia são irmãs dentro do
 * corpo da folha, que não tem espaço entre filhos (medido no navegador: as
 * alturas somam exatamente a caixa de conteúdo). Antes havia um respiro de 3 mm
 * somado aqui, e o mesmo respiro de novo entre o cabeçalho do dia e a escrita —
 * 6 mm de papel que o motor reservava e a folha usava, o que fazia a última
 * linha da lista do dia não caber por um triz.
 */
export const ESPACO_FAIXA_MM = ALTURA_FAIXA_MM;
/**
 * Reserva de altura para o bloco de título de uma seção (título + subtítulo).
 *
 * É usado para calcular quantas entradas cabem numa página, não para limitar a
 * seção: o título abraça o próprio conteúdo.
 */
export const TITULO_SECAO_MM = 28;
/**
 * Quanto da linha um rótulo pode encher antes de contar como duas linhas.
 *
 * A largura média do caractere é uma **média**: em texto curto e maiúsculo — que
 * é o caso do rótulo de uma caixa — o erro de alguns por cento decide entre uma
 * e duas linhas, e errar para baixo corta. Rótulo que passa de 85% da linha
 * reservada é contado como duas.
 */
export const MARGEM_DE_QUEBRA_DO_ROTULO = 0.85;
/**
 * Altura útil da folha, já sem cabeçalho, rodapé e uma folga de segurança.
 *
 * É a mesma conta do caderno de páginas extras: quem mede conteúdo antes de
 * desenhar precisa partir daqui para não estourar a margem.
 */
function alturaUtilDoMiolo(ctx) {
    return ctx.medidas.alturaConteudoMm - ALTURA_CABECALHO_PAGINA_MM - ALTURA_RODAPE_MM - 2.5;
}
/**
 * Folga somada às alturas calculadas pelo conteúdo.
 *
 * As contas de texto erram para menos de propósito (a largura do caractere é
 * estimada), então uma caixa dimensionada por elas precisa de um respiro — um
 * corte de meio milímetro na última linha já é visível no papel.
 */
const FOLGA_DO_CONTEUDO_MM = 1.5;
/** Vão entre dois blocos empilhados numa coluna, em milímetros (o do desenho). */
const VAO_ENTRE_BLOCOS_MM = 2.2;
/* ------------------------------------------------------------------ */
/* Tipografia                                                          */
/* ------------------------------------------------------------------ */
/**
 * Ajuste da conta de largura conforme a fonte que o usuário escolheu pelo nome.
 *
 * A regra é a mesma do desenho (`apps/web/src/impressao/BlocoImpresso.tsx`):
 * **DISPLAY e XL** saem na fonte de título; todo o resto sai na fonte de texto.
 * Se as duas contas divergirem, uma caixa dimensionada pelo motor corta o que o
 * desenho escreveu a mais.
 */
function larguraRelativaDoTexto(config, tamanho) {
    const titulo = tamanho === 'DISPLAY' || tamanho === 'XL';
    const nome = titulo ? config.tipografia?.fonteTitulo : config.tipografia?.fonteTexto;
    return FATOR_LARGURA_FONTE[nome ?? 'SISTEMA'] ?? 1;
}
/**
 * Altura que o cabeçalho do dia precisa para não cortar o que desenha.
 *
 * Quem manda é a coluna da esquerda: dia da semana, número do dia e mês. O
 * número é o maior texto da página (DISPLAY, ou XL com dois dias), e o tamanho
 * dele acompanha a largura da folha — num papel baixo e largo ele passa da
 * fração de altura e some pelo `overflow: hidden`.
 *
 * A coluna da direita (semana e trimestre, e às vezes o feriado) não entra na
 * conta: são três linhas de texto miúdo, sempre mais baixas que o número.
 */
function alturaNaturalDoCabecalhoDia(config, nivel, medidas, escala) {
    const tamanhoNumero = config.diasPorPagina === 2 ? 'XL' : 'DISPLAY';
    const larguraDaColuna = Math.max(20, medidas.larguraConteudoMm * 0.62 - 6);
    const emMm = (texto, tamanho, tracking = 0) => alturaDoTextoMm(texto, config.formato, tamanho, larguraDaColuna, escala, tracking, larguraRelativaDoTexto(config, tamanho));
    const datado = emMm('QUARTA-FEIRA', 'XS', 0.16) +
        emMm('30', tamanhoNumero) +
        emMm('setembro de 2026', 'XS', 0.1) +
        2 * VAO_ENTRE_BLOCOS_MM;
    /*
     * A coluna da direita: o nome do feriado (quando há feriados marcados), a
     * semana e o trimestre. É mais baixa que a esquerda nos formatos folgados, mas
     * num A6 com o feriado aceso ela encosta — e a página cortava por 0,8 mm.
     */
    const linhasDaDireita = config.feriados === 'NENHUM' ? 2 : 3;
    const larguraDaDireita = Math.max(20, medidas.larguraConteudoMm * 0.38 - 6);
    const direita = linhasDaDireita *
        alturaDoTextoMm('Semana 30', config.formato, 'XS', larguraDaDireita, escala, 0.1, larguraRelativaDoTexto(config, 'XS')) +
        (linhasDaDireita - 1) * VAO_ENTRE_BLOCOS_MM;
    // Agenda permanente: o rótulo “Data”, o campo para escrever e a régua de siglas.
    const permanente = emMm('Data', 'XS', 0.18) +
        emMm(PADRAO_DATA[nivel], TAMANHO_DATA[nivel]) +
        VAO_ENTRE_BLOCOS_MM +
        10;
    const conteudo = Math.max(config.calendario.modo === 'PERMANENTE' ? Math.max(datado, permanente) : datado, direita);
    /*
     * Sem folga extra aqui: a conta de texto já erra **para mais** (a entrelinha
     * cheia de cada tamanho é maior que a linha desenhada), e no A6 cada milímetro
     * do cabeçalho sai da coluna lateral — foi assim que a caixa de “Gratidão”
     * passou a cortar 0,8 mm quando o cabeçalho cresceu.
     */
    return conteudo + PREENCHIMENTO_DO_CABECALHO_MM * 2;
}
/** Preenchimento interno do cabeçalho do dia, em milímetros. */
const PREENCHIMENTO_DO_CABECALHO_MM = 3;
function nivelDe(alturaDiaMm) {
    if (alturaDiaMm >= 110)
        return 'AMPLO';
    if (alturaDiaMm >= 80)
        return 'COMPACTO';
    return 'MINIMO';
}
/**
 * Altura que o rótulo da pauta rouba da lista, em milímetros.
 *
 * O bloco de escrita do dia tem título ("Lista do dia", "Anotações"...). Medido
 * no navegador em A4: 3,85 mm de texto mais 1,2 mm de margem. A reserva é maior
 * que a medida de propósito — errar para menos desenha uma linha a mais e a
 * última caixinha de data sai cortada ao meio, que é justamente o defeito que
 * esta conta existe para evitar.
 */
const RESERVA_ROTULO_MM = 6;
/**
 * Quantas linhas de data cabem numa altura.
 *
 * A grade de linhas com data desenha caixinhas de DOM, uma por linha, e o que
 * passa da caixa é escondido pelo `overflow: hidden` — sem aviso. Por isso a
 * conta é feita aqui, com a altura que o próprio motor já calculou, e não no
 * desenho: o desenho não tem como medir nada (a área de impressão nasce com
 * `display: none`, e no layout não há altura nenhuma para consultar).
 *
 * Arredonda para baixo: sobrar um pedaço de linha é discreto, cortar uma caixa
 * de data ao meio não é.
 */
export function linhasQueCabem(alturaDisponivelMm, passoMm) {
    if (passoMm <= 0)
        return 1;
    return Math.max(1, Math.floor(alturaDisponivelMm / passoMm));
}
/** Modo de feriados da config, com a UF só fazendo sentido no modo estadual. */
function modoDeFeriados(config) {
    return (config.feriados ?? 'NENHUM');
}
/**
 * Feriados do mês prontos para o mini calendário.
 *
 * Sem modo escolhido (ou sem UF no modo estadual) a lista sai vazia e o
 * calendário fica exatamente como era antes do recurso.
 */
function feriadosDe(config, ano, mes) {
    const modo = modoDeFeriados(config);
    if (modo === 'NENHUM')
        return [];
    return feriadosDoMes(ano, mes, modo, config.uf || undefined).map((feriado) => ({
        dia: feriado.dia,
        nome: feriado.nome,
        tipo: feriado.tipo,
        facultativo: feriado.facultativo,
    }));
}
function criarContexto(config) {
    const paleta = construirPaleta(config.cores);
    const papel = PAPEIS[config.formato];
    const medidas = medirPaginaDoMiolo(config);
    const temFaixa = quadrosLigados(config.quadros, 'FAIXA').length > 0;
    const alturaUtil = medidas.alturaConteudoMm - ALTURA_RODAPE_MM;
    /**
     * A faixa de quadros rápidos rouba altura do dia. Como o nível de densidade
     * depende dessa altura, calculamos em duas etapas: primeiro com a faixa, e se
     * o resultado ficar mínimo demais, devolvemos o espaço e ficamos sem faixa.
     */
    const calcular = (comFaixa) => {
        const util = alturaUtil - (comFaixa ? ESPACO_FAIXA_MM : 0);
        // No modo semanal cada dia é uma faixa da folha: sete faixas com folga.
        if (config.paginasDeDia === 'SEMANA')
            return (util - 6 * 2) / 7;
        if (config.paginasDeDia === 'NENHUM')
            return util;
        return config.diasPorPagina === 1 ? util : (util - GAP_DIAS_MM) / 2;
    };
    let usarFaixa = temFaixa;
    let alturaDiaMm = calcular(usarFaixa);
    let nivel = nivelDe(alturaDiaMm);
    if (usarFaixa && nivel === 'MINIMO') {
        usarFaixa = false;
        alturaDiaMm = calcular(false);
        nivel = nivelDe(alturaDiaMm);
    }
    const alturaCabecalhoDiaMm = Number(Math.max(
    /*
     * Duas contas, e vale a maior.
     *
     * A fração é o respiro do cabeçalho nos formatos folgados — mexer nela
     * mudaria todas as páginas de A4 e A5 já impressas. O conteúdo é o piso que
     * impede o corte: em papel baixo e largo (o quadrado) e no A6 com dois dias
     * por página, a fração fica menor que o número do dia.
     */
    Math.min(Math.max(alturaDiaMm * 0.17, 13), config.paginasDeDia === 'SEMANA' ? 16 : config.diasPorPagina === 1 ? 34 : 24), alturaNaturalDoCabecalhoDia(config, nivel, medidas, config.tipografia?.escala ?? 1)).toFixed(1));
    /**
     * Altura útil de escrita: o que sobra do dia depois do cabeçalho dele.
     *
     * O corpo da folha empilha cabeçalho, escrita e faixa sem espaço entre eles, e
     * o rodapé já saiu de `util`. Só o cabeçalho do dia precisa sair daqui — antes
     * saía também um respiro de 3 mm que não existe, e a conta entregava 6 mm menos
     * do que a folha realmente dava para escrever.
     *
     * No formato semanal, o que interessa é o espaço de cada dia: a faixa da folha
     * menos o título do dia.
     */
    const alturaEscritaMm = config.paginasDeDia === 'SEMANA'
        ? Number((alturaDiaMm - 5).toFixed(1))
        : Number((alturaDiaMm - alturaCabecalhoDiaMm).toFixed(1));
    // Se o espaço é curto, apertamos um pouco a pauta para caber mais linhas.
    const alturaLinhaMm = Number(Math.min(config.escrita.alturaLinha, Math.max(4, alturaEscritaMm / 9)).toFixed(2));
    return {
        config,
        paleta,
        papel,
        medidas,
        nivel,
        alturaDiaMm: Number(alturaDiaMm.toFixed(1)),
        alturaCabecalhoDiaMm,
        alturaEscritaMm,
        alturaLinhaMm,
        espacamentoMm: config.escrita.espacamento,
        cursorConteudo: 0,
    };
}
/* ------------------------------------------------------------------ */
/* Pedaços reutilizados                                                */
/* ------------------------------------------------------------------ */
function rodapeDe(ctx, numeroPagina) {
    return {
        esquerda: ctx.config.nome.slice(0, 46),
        centro: ctx.config.numerarPaginas && numeroPagina ? String(numeroPagina) : undefined,
        mostrarRegua: false,
    };
}
function cabecalhoDe(ctx, esquerda, direita) {
    return { esquerda: esquerda.slice(0, 40), direita: direita?.slice(0, 40), mostrarRegua: true };
}
/** Seção de título usada em todas as páginas que não são de dia. */
function secaoTitulo(titulo, subtitulo) {
    const blocos = [
        texto(titulo, { tamanho: 'XL', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
    ];
    if (subtitulo) {
        blocos.push(texto(subtitulo, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }));
    }
    blocos.push(separador({ estilo: 'LINHA', cor: 'DESTAQUE', espessuraMm: 0.4, espacoMm: 3 }));
    return secao('titulo', blocos, { peso: 0 });
}
function fundoClaro() {
    return { tipo: 'COR', temaEscuro: false };
}
/* ------------------------------------------------------------------ */
/* Capa                                                               */
/* ------------------------------------------------------------------ */
function construirCapa(ctx) {
    const { config, paleta } = ctx;
    // Arte final enviada pelo usuário: a página é a imagem, sem mais nada.
    if (config.capas?.frente)
        return paginaDeArte(ctx, 'CAPA', 'capa', config.nome, config.capas.frente);
    const temaEscuro = true;
    const conteudoCapa = [
        texto(config.dadosPessoais.nome, { tamanho: 'LG', peso: 'FORTE', cor: 'INVERSO' }),
    ];
    if (config.dadosPessoais.telefone) {
        conteudoCapa.push(texto(config.dadosPessoais.telefone, { tamanho: 'SM', cor: 'INVERSO' }));
    }
    if (config.dadosPessoais.email) {
        conteudoCapa.push(texto(config.dadosPessoais.email, { tamanho: 'XS', cor: 'INVERSO' }));
    }
    const blocoDados = [];
    if (config.dadosPessoais.mostrarNaCapa && config.dadosPessoais.nome) {
        blocoDados.push(caixa({
            titulo: 'Este caderno pertence a',
            conteudo: conteudoCapa,
            fundo: 'NENHUM',
            borda: 'INVERSO',
            raioMm: 3,
            preenchimentoMm: 5,
            corTitulo: 'INVERSO',
            alturaMm: 34,
        }));
    }
    /*
     * Miolo sem páginas de dia não é agenda: ele fica com as páginas extras e os
     * cadernos. A capa dizia "AGENDA" em qualquer caso, o que ficava errado nesse
     * tipo de miolo.
     */
    const semDias = config.paginasDeDia === 'NENHUM';
    const periodo = semDias
        ? ''
        : config.calendario.modo === 'DATADO'
            ? D.rotuloPeriodo(config.calendario.inicio, config.calendario.fim)
            : 'Agenda permanente';
    return {
        id: 'capa',
        formato: config.formato,
        tipo: 'CAPA',
        titulo: config.nome,
        meta: {},
        fundo: {
            tipo: 'GRADIENTE',
            corA: paleta.principalEscuro,
            // Misturamos o destaque com a principal escura para o degradê atravessar
            // um tom intermediário em vez de brigar entre duas cores distantes.
            corB: misturar(paleta.principalEscuro, paleta.destaque, 0.55),
            angulo: 148,
            temaEscuro,
        },
        secoes: [
            secao('capa-topo', [
                texto(periodo, {
                    tamanho: 'XS',
                    cor: 'INVERSO',
                    maiusculas: true,
                    tracking: 0.22,
                    peso: 'FORTE',
                }),
            ], { alturaMm: 12, peso: 0 }),
            secao('capa-centro', [
                texto(semDias ? 'CADERNO' : 'AGENDA', {
                    tamanho: 'MD',
                    cor: 'INVERSO',
                    maiusculas: true,
                    tracking: 0.42,
                    peso: 'FORTE',
                    alinhamento: 'CENTRO',
                    espacoAbaixoMm: 4,
                }),
                separador({ estilo: 'ORNAMENTO', cor: 'INVERSO', espacoMm: 5 }),
                texto(config.nome, {
                    tamanho: 'DISPLAY',
                    cor: 'INVERSO',
                    peso: 'FORTE',
                    alinhamento: 'CENTRO',
                    entrelinha: 1.05,
                    espacoAcimaMm: 2,
                }),
                texto(subtituloDaCapa(ctx), {
                    tamanho: 'SM',
                    cor: 'INVERSO',
                    alinhamento: 'CENTRO',
                    espacoAcimaMm: 4,
                }),
            ], { peso: 1, alinhamentoVertical: 'CENTRO' }),
            secao('capa-base', blocoDados.length ? blocoDados : [espaco(30)], {
                alturaMm: blocoDados.length ? 40 : 30,
                peso: 0,
            }),
        ],
    };
}
function subtituloDaCapa(ctx) {
    const { config } = ctx;
    const partes = [ctx.papel.rotulo.split(' ')[0] ?? 'A4'];
    if (config.papel?.orientacao === 'PAISAGEM')
        partes.push('paisagem');
    partes.push(descricaoDoModoDeDia(config));
    if (config.estilo === 'DEVOCIONAL')
        partes.push('devocional');
    if (config.estilo === 'MOTIVACIONAL')
        partes.push('motivacional');
    return partes.join(' · ');
}
/** Texto curto que descreve como as páginas de dia estão montadas. */
function descricaoDoModoDeDia(config) {
    switch (config.paginasDeDia) {
        case 'SEMANA':
            return '7 dias por página';
        case 'NENHUM':
            return 'sem páginas de dia';
        case 'DIA':
        default:
            return config.diasPorPagina === 1 ? '1 dia por página' : '2 dias por página';
    }
}
/* ------------------------------------------------------------------ */
/* Capas prontas (arte enviada)                                        */
/* ------------------------------------------------------------------ */
/**
 * Página feita só da arte enviada: sem cabeçalho, sem rodapé, sem margem — a
 * imagem encosta nas quatro bordas do papel, como numa capa impressa.
 */
function paginaDeArte(ctx, tipo, id, titulo, url) {
    return {
        id,
        formato: ctx.config.formato,
        tipo,
        titulo,
        meta: {},
        fundo: fundoClaro(),
        secoes: [
            secao(`${id}-arte`, [
                imagem(url, {
                    ajuste: 'COBRIR',
                    moldura: false,
                    preenchimentoMm: 0,
                    flexivel: true,
                }),
            ], { peso: 1, preenchimentoMm: 0, alinhamentoVertical: 'CENTRO' }),
        ],
    };
}
/**
 * Contracapa: a arte enviada ou uma página em branco para quem vai colar a
 * arte depois. Vem sempre no fim do miolo.
 */
function construirContracapa(ctx) {
    const arte = ctx.config.capas?.verso;
    if (arte)
        return paginaDeArte(ctx, 'CONTRACAPA', 'contracapa', 'Contracapa', arte);
    return {
        id: 'contracapa',
        formato: ctx.config.formato,
        tipo: 'CONTRACAPA',
        titulo: 'Contracapa',
        meta: {},
        fundo: fundoClaro(),
        secoes: [secao('contracapa-branca', [], { peso: 1 })],
    };
}
/* ------------------------------------------------------------------ */
/* Meus dados                                                          */
/* ------------------------------------------------------------------ */
function construirDadosPessoais(ctx) {
    const { config } = ctx;
    const dados = config.dadosPessoais;
    const alturaLinha = Math.max(6, ctx.alturaLinhaMm);
    /**
     * Formulário de papel: cada informação fica escrita ABAIXO do risco, que
     * ocupa toda a largura da página. As máscaras (`(00) 00000-0000`,
     * `__/__/______`) entram como guia leve sobre a linha, para quem tem letra
     * grande não apertar os números.
     */
    const linha = (rotulo, valor, mascara) => ({
        rotulo,
        valor: valor?.trim() ? valor : undefined,
        mascara,
    });
    return {
        id: 'dados-pessoais',
        formato: config.formato,
        tipo: 'DADOS_PESSOAIS',
        titulo: 'Meus dados',
        meta: {},
        indice: 'Meus dados',
        cabecalho: cabecalhoDe(ctx, config.nome, config.formato),
        rodape: rodapeDe(ctx),
        fundo: fundoClaro(),
        secoes: [
            secaoTitulo('Meus dados', 'Preencha com caneta e confira antes de levar para a gráfica.'),
            secao('dados-pessoais-corpo', [
                campos([
                    linha('Nome completo', dados.nome),
                    linha('Data de nascimento', dados.nascimento, MASCARAS_DADOS.data),
                    linha('Telefone / WhatsApp', dados.telefone, MASCARAS_DADOS.telefone),
                    linha('E-mail', dados.email, MASCARAS_DADOS.email),
                ], { alturaLinhaMm: alturaLinha, rotuloAbaixo: true }),
                campos([
                    linha('Endereço', dados.endereco),
                    linha('Cidade / UF', dados.cidade),
                    linha('CEP', dados.cep, MASCARAS_DADOS.cep),
                ], { alturaLinhaMm: alturaLinha, rotuloAbaixo: true }),
                caixa({
                    titulo: 'Contato de emergência',
                    conteudo: [
                        campos([
                            linha('Nome', dados.contatoEmergencia),
                            linha('Parentesco ou relação', dados.relacaoEmergencia),
                            linha('Telefone', dados.telefoneEmergencia, MASCARAS_DADOS.telefone),
                        ], { alturaLinhaMm: alturaLinha, rotuloAbaixo: true }),
                    ],
                    fundo: 'SUAVE',
                    raioMm: 3,
                    preenchimentoMm: 4,
                    /*
                     * A fração da página é o respiro de sempre; o conteúdo é o piso.
                     * Em A6 a fração dava 22 mm e o formulário pedia 39 mm — a caixa
                     * cortava o próprio conteúdo em silêncio (achado pelo teste dos
                     * ícones, que mediu A6 com dois dias por página e linhas com data).
                     */
                    alturaMm: Math.round(Math.max(ctx.alturaEscritaMm * 0.3, alturaDoTituloDaCaixa(ctx, 'Contato de emergência', ctx.medidas.larguraConteudoMm - 8) +
                        1.4 +
                        alturaDeCamposMm(3, alturaLinha, true) +
                        8 +
                        FOLGA_DO_CONTEUDO_MM)),
                }),
                caixa({
                    titulo: 'Se encontrar este caderno',
                    conteudo: [
                        texto(dados.avisoPerda ||
                            'Este caderno é importante para mim. Por favor, entre em contato pelo telefone acima e combinamos a devolução. Muito obrigado!', { tamanho: 'XS', cor: 'TEXTO' }),
                        campos([linha('Recado', '')], { alturaLinhaMm: alturaLinha, rotuloAbaixo: true }),
                    ],
                    fundo: 'DESTAQUE_SUAVE',
                    borda: 'DESTAQUE',
                    raioMm: 3,
                    preenchimentoMm: 4,
                    flexivel: true,
                }),
            ], { peso: 1, gapMm: 4 }),
        ],
    };
}
/* ------------------------------------------------------------------ */
/* Visão do ano                                                        */
/* ------------------------------------------------------------------ */
/**
 * Proporção do mini calendário: altura ≈ 1,35 × largura.
 *
 * O desenho do calendário é todo relativo à largura da célula (`cqw` no
 * renderizador): quanto mais larga a célula, mais alto o calendário. A conta
 * abaixo usa esta proporção para decidir quantos meses cabem na folha.
 *
 * O número saiu de medição no navegador (A4, 56 mm de célula, 68 mm de altura) e
 * foi arredondado para cima de propósito: errar para mais só diminui a grade,
 * errar para menos corta o fim da página — que era exatamente o defeito.
 */
const PROPORCAO_MINI_CALENDARIO = 1.35;
/** Vão entre as células da grade do ano, em milímetros. */
const GAP_GRADE_ANO_MM = 4;
/**
 * Célula menor que isto fica ilegível: sobram menos de 4 mm para cada dia do mês.
 */
const LARGURA_MINIMA_DA_CELULA_MM = 24;
/**
 * Duas folhas por ano é o teto.
 *
 * O panorama existe para ser lido de uma olhada: passando de duas folhas, ele
 * deixa de ser panorama. Dentro desse teto a grade escolhe a célula mais larga
 * possível — calendário grande lê-se melhor que calendário apertado numa folha
 * só.
 */
const PAGINAS_MAXIMAS_DO_ANO = 2;
/**
 * Meses por folha na visão do ano.
 *
 * Seis é o que mantém o mês largo e as duas folhas **equilibradas** — a conta
 * antiga crescia o número de meses até encher a folha, e no papel estreito do
 * Slim isso deu 9 meses numa folha e 3 na outra, com o mini calendário virado
 * num borrão. O limite vale para qualquer papel; quem manda no resto é a largura
 * da célula.
 */
const MESES_POR_FOLHA_DO_ANO = 6;
/**
 * Escolhe a grade da visão do ano.
 *
 * O ponto de partida é que o mini calendário cresce com a largura da célula:
 * quanto mais colunas, menor a célula e mais baixo o calendário. Por isso a
 * grade depende do papel, e não de um número fixo:
 *
 *  - 12 meses em 3 colunas pediam 68 mm de altura em células de 56 mm de largura
 *    e as quatro linhas somavam 272 mm numa folha A4 com 230 mm úteis. O
 *    renderizador encolhia as células e o fim de todo mês saía cortado;
 *  - 12 meses em 4 colunas cabiam numa folha só, mas deixavam o calendário do
 *    A5 com 27 mm de largura, apertado para ler.
 *
 * A regra agora é: testamos as grades da mais larga para a mais estreita e
 * ficamos com a primeira que resolve o ano em até duas folhas. Se nenhuma
 * conseguir, aí sim escolhemos a que gasta menos folhas.
 */
function gradeDoAno(larguraConteudoMm, alturaDisponivelMm) {
    let maisEstreita = null;
    let menorQuantidadeDePaginas = Number.POSITIVE_INFINITY;
    for (const colunas of [2, 3, 4, 5, 6]) {
        const larguraDaCelula = (larguraConteudoMm - GAP_GRADE_ANO_MM * (colunas - 1)) / colunas;
        if (larguraDaCelula < LARGURA_MINIMA_DA_CELULA_MM)
            continue;
        const alturaDaCelula = larguraDaCelula * PROPORCAO_MINI_CALENDARIO;
        const linhas = Math.max(1, Math.floor((alturaDisponivelMm + GAP_GRADE_ANO_MM) / (alturaDaCelula + GAP_GRADE_ANO_MM)));
        const mesesPorPagina = Math.min(MESES_POR_FOLHA_DO_ANO, linhas * colunas);
        const paginas = Math.ceil(12 / mesesPorPagina);
        const candidata = {
            colunas,
            mesesPorPagina,
            alturaDaGradeMm: linhas * alturaDaCelula + (linhas - 1) * GAP_GRADE_ANO_MM,
        };
        // A primeira grade (a mais larga) que resolve o ano em duas folhas vence.
        if (paginas <= PAGINAS_MAXIMAS_DO_ANO)
            return candidata;
        if (paginas < menorQuantidadeDePaginas) {
            menorQuantidadeDePaginas = paginas;
            maisEstreita = candidata;
        }
    }
    // Só chega aqui uma folha minúscula, em que nem duas colunas legíveis cabem.
    return (maisEstreita ?? {
        colunas: 2,
        mesesPorPagina: MESES_POR_FOLHA_DO_ANO,
        alturaDaGradeMm: alturaDisponivelMm,
    });
}
/**
 * Visão do ano.
 *
 * Além do ano de referência, o usuário pode pedir os anos anteriores e
 * seguintes (ex.: um ano para trás e dois para frente), o que gera uma visão
 * por ano — útil para planejar feriados, viagens e prazos que atravessam a
 * virada do ano.
 */
function construirAno(ctx) {
    const { config } = ctx;
    const ano = config.calendario.ano;
    const anos = anosDaAgenda(config.calendario);
    /** Altura que sobra na folha depois do título. */
    const espacoDoAnoMm = alturaUtilDoMiolo(ctx) - TITULO_SECAO_MM;
    const { colunas, mesesPorPagina, alturaDaGradeMm } = gradeDoAno(ctx.medidas.larguraConteudoMm, espacoDoAnoMm);
    /**
     * A grade cresce até preencher a folha.
     *
     * A altura calculada é o mínimo que o desenho pede; o que sobra vira mês mais
     * alto, com as semanas mais espaçadas. Sem isso a visão do ano do A4 parava em
     * 155 mm de uma folha de 297 mm e ficava um pé de página vazio de quase 10 cm.
     * O piso continua sendo a altura pedida: a grade nunca encolhe, porque encolher
     * é o que cortava o fim do mês.
     */
    const alturaDaGradeFinalMm = Math.max(alturaDaGradeMm, espacoDoAnoMm);
    const paginas = [];
    for (const anoDaVisao of anos) {
        for (let inicio = 0; inicio < 12; inicio += mesesPorPagina) {
            const recorte = Array.from({ length: Math.min(mesesPorPagina, 12 - inicio) }, (_, i) => inicio + i + 1);
            const titulo = recorte.length === 12
                ? `Visão de ${anoDaVisao}`
                : `${D.nomeMes(recorte[0], 'MAIUSCULO')} a ${D.nomeMes(recorte[recorte.length - 1], 'MAIUSCULO')} de ${anoDaVisao}`;
            paginas.push({
                id: `ano-${anoDaVisao}-${inicio}`,
                formato: config.formato,
                tipo: 'ANO',
                titulo,
                meta: { ano: anoDaVisao },
                indice: recorte.length === 12 ? `Visão de ${anoDaVisao}` : titulo,
                cabecalho: cabecalhoDe(ctx, config.nome, String(anoDaVisao)),
                rodape: rodapeDe(ctx),
                fundo: fundoClaro(),
                secoes: [
                    secaoTitulo(titulo, anoDaVisao === ano
                        ? 'Panorama rápido para planejar feriados, viagens e prazos.'
                        : 'Ano de referência para planejar feriados, viagens e prazos.'),
                    secao('ano-grade', [], {
                        peso: 1,
                        colunas: [
                            coluna(1, [
                                grupo(recorte.map((mes) => miniCalendario(anoDaVisao, mes, {
                                    rotulo: D.nomeMes(mes, 'MAIUSCULO'),
                                    mostrarNumeroSemana: false,
                                    semDatas: config.calendario.modo === 'PERMANENTE',
                                    feriados: feriadosDe(config, anoDaVisao, mes),
                                    mostrarNomesFeriados: false,
                                })), { colunas, gapMm: GAP_GRADE_ANO_MM, flexivel: false, alturaMm: alturaDaGradeFinalMm }),
                            ]),
                        ],
                    }),
                ],
            });
        }
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Divisória de mês                                                    */
/* ------------------------------------------------------------------ */
/**
 * Altura da faixa de título da divisória de mês.
 *
 * Como no cabeçalho do dia, vale o maior entre a fração da folha (o respiro que
 * os formatos folgados já tinham) e o que o conteúdo pede — o mês em DISPLAY é
 * o maior texto do miolo, e num papel baixo e largo ele passa da fração.
 */
function alturaDoTituloDoMes(ctx, titulo, mes) {
    const fracao = Math.round(ctx.alturaEscritaMm * 0.28);
    const escala = ctx.config.tipografia?.escala ?? 1;
    const largura = Math.max(20, ctx.medidas.larguraConteudoMm - 6);
    const emMm = (texto, tamanho, tracking = 0) => alturaDoTextoMm(texto, ctx.config.formato, tamanho, largura, escala, tracking, larguraRelativaDoTexto(ctx.config, tamanho));
    const conteudo = 
    // O número do mês junto do ano: "03 · 2026".
    emMm(`${String(mes).padStart(2, '0')} · ${String(ctx.config.calendario.ano)}`, 'XS', 0.3) +
        emMm(titulo, 'DISPLAY') +
        // Separador: traço de 0,5 mm mais 3 mm de respiro de cada lado.
        6.5 +
        // O mote do mês, medido pelo mesmo caminho do desenho: frase comprida
        // quebra em duas linhas e a faixa cresce em vez de cortar.
        emMm(moteDoMes(mes), 'SM') +
        emMm('Do zero ao fim do ano, uma semana por vez.', 'SM') +
        // Cinco blocos empilhados, com o vão da seção entre eles.
        4 * 3;
    return Math.round(Math.max(fracao, conteudo + FOLGA_DO_CONTEUDO_MM));
}
/**
 * Altura do título de uma caixa, em milímetros — a mesma fonte que o desenho usa.
 *
 * Três cuidados, todos nascidos de defeito medido:
 *
 * 1. o texto é medido **em maiúsculas**, porque é assim que ele sai no papel (o
 *    desenho aplica `text-transform: uppercase` no título da caixa);
 * 2. a largura recebida é a **de dentro da caixa** (o preenchimento é de quem
 *    chama), porque é a largura que o rótulo tem de verdade;
 * 3. rótulo que enche quase toda a linha conta como **duas linhas**. A largura
 *    média do caractere é uma média, e rótulo é texto curto e maiúsculo — justo
 *    onde a média erra mais. Sem esta regra, com uma fonte caligráfica escolhida
 *    pelo usuário, "Datas importantes" ocupava duas linhas no desenho enquanto o
 *    motor reservava uma, e a grade de datas da divisória de mês saía 4 mm mais
 *    alta que a caixa — cortada, em silêncio (achado pelo teste das fontes
 *    extremas, em A6 com linhas com data).
 */
function alturaDoTituloDaCaixa(ctx, titulo, larguraMm) {
    const texto = titulo.toUpperCase();
    const escala = ctx.config.tipografia?.escala ?? 1;
    const larguraRelativa = larguraRelativaDoTexto(ctx.config, 'XS');
    const pt = tamanhoFontePt(ctx.config.formato, 'XS', escala, 0.14);
    const porLinha = Math.max(8, Math.floor(larguraMm / (pt * 0.3528 * 0.5 * larguraRelativa)));
    const apertado = texto.length > porLinha * MARGEM_DE_QUEBRA_DO_ROTULO;
    const linhas = Math.max(linhasDoTexto(texto, pt, larguraMm, larguraRelativa), apertado ? 2 : 1);
    return Number((linhas * pt * 0.3528 * ENTRELINHA_PADRAO.XS).toFixed(2));
}
/**
 * Altura de um bloco de campos, em milímetros — a mesma conta do desenho.
 *
 * O renderizador dimensiona o bloco de campos por linha
 * (`apps/web/src/impressao/BlocoImpresso.tsx`): com o rótulo **abaixo** do risco
 * — o caso do formulário de "Meus dados" — cada item reserva `alturaLinhaMm`
 * mais 3 mm para o rótulo; com o rótulo ao lado, é uma linha por item e 1,4 mm
 * de vão entre elas.
 *
 * O motor precisa da mesma conta para dimensionar a caixa pelo conteúdo. Foi a
 * falta dela que deixou a caixa "Contato de emergência" cortada em A6: a altura
 * dela vinha só de uma fração da página, e o conteúdo pedia mais.
 */
function alturaDeCamposMm(itens, alturaLinhaMm, rotuloAbaixo) {
    if (rotuloAbaixo)
        return itens * (alturaLinhaMm + 3);
    return itens * alturaLinhaMm + Math.max(0, itens - 1) * 1.4;
}
function construirDivisoriaMes(ctx, ano, mes, opcoes) {
    const { config, paleta } = ctx;
    const mesNome = D.nomeMes(mes);
    const titulo = `${D.nomeMes(mes, 'MAIUSCULO')}`;
    const dias = opcoes.destacar ?? [];
    /*
     * Os feriados do mês são calculados uma vez e usados duas vezes: dentro do
     * mini calendário (para marcar os dias) e na lista escrita abaixo dele. A
     * mesma configuração decide os dois — é o `feriados` do passo de período.
     */
    const feriados = feriadosDe(config, ano, mes);
    /*
     * Duas contas em sequência, e não duas frações da folha.
     *
     * A faixa do título sai do próprio conteúdo; o que sobra é o corpo. As caixas
     * do corpo saem de uma fração **desse resto** — antes saíam de uma fração da
     * área de escrita, que não sabia quanto o título tinha levado. A diferença era
     * o corte de 1 a 5 mm no A6, escondido pelo `overflow: hidden`.
     */
    const alturaDoTitulo = alturaDoTituloDoMes(ctx, titulo, mes);
    const alturaDoCorpo = Math.max(40, ctx.alturaEscritaMm - alturaDoTitulo);
    const larguraDaColuna = Math.max(20, ctx.medidas.larguraConteudoMm * 0.52 - 8);
    const tituloDeDatas = 'Datas importantes';
    /*
     * O título é medido na largura de **dentro** da caixa: são 4 mm de
     * preenchimento de cada lado (o mesmo número do `preenchimentoMm` da caixa
     * abaixo). Medir pela largura da coluna dava ao rótulo 8 mm que ele não tem.
     */
    const larguraDoRotulo = larguraDaColuna - 8;
    /*
     * Da caixa de datas, o título, o vão de 1,4 mm e o preenchimento de 4 mm de
     * cada lado saem primeiro: a grade de datas fica com o que sobra.
     *
     * O piso de três linhas é o que a caixa precisa para não cortar a si mesma —
     * em A6 a fração do corpo dá menos que isso, e a caixa cresce para caber. Quem
     * cede é a caixa de cima, que é a flexível.
     */
    const alturaDaGradeDeDatas = Math.max(ctx.alturaLinhaMm * 3, Math.round(alturaDoCorpo * 0.3) - alturaDoTituloDaCaixa(ctx, tituloDeDatas, larguraDoRotulo) - 1.4 - 8);
    const alturaDaCaixaDeDatas = Math.round(alturaDaGradeDeDatas + alturaDoTituloDaCaixa(ctx, tituloDeDatas, larguraDoRotulo) + 1.4 + 8);
    return {
        id: `mes-${ano}-${mes}`,
        formato: config.formato,
        tipo: 'MES',
        titulo: mesNome,
        meta: { ano, mes },
        indice: `${D.nomeMes(mes)} ${ano}`,
        cabecalho: cabecalhoDe(ctx, config.nome, String(ano)),
        rodape: rodapeDe(ctx),
        fundo: {
            tipo: 'COR',
            temaEscuro: false,
        },
        secoes: [
            secao('mes-titulo', [
                texto(`${String(mes).padStart(2, '0')} · ${String(ano)}`, {
                    tamanho: 'XS',
                    cor: 'DESTAQUE_TEXTO',
                    maiusculas: true,
                    tracking: 0.3,
                    peso: 'FORTE',
                }),
                texto(titulo, { tamanho: 'DISPLAY', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO', entrelinha: 1 }),
                separador({ estilo: 'LINHA', cor: 'DESTAQUE', espessuraMm: 0.5, espacoMm: 3 }),
                /*
                 * O mote do mês: a frase que abre a divisória, em itálico e na cor de
                 * destaque. A altura da faixa é medida com ele (esta é a única fonte
                 * da conta), então a frase longa empurra a faixa em vez de ser cortada.
                 */
                texto(moteDoMes(mes), { tamanho: 'SM', cor: 'DESTAQUE_TEXTO', italico: true }),
                texto(opcoes.assinatura, { tamanho: 'SM', cor: 'TEXTO_SUAVE', italico: true }),
            ], { alturaMm: alturaDoTitulo, peso: 0 }),
            secao('mes-corpo', [], {
                peso: 1,
                gapMm: 5,
                colunas: [
                    coluna(0.52, [
                        caixa({
                            titulo: 'Como quero que este mês seja',
                            conteudo: [linhas({ alturaLinhaMm: ctx.alturaLinhaMm, flexivel: true })],
                            fundo: 'SUAVE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            flexivel: true,
                        }),
                        caixa({
                            titulo: tituloDeDatas,
                            conteudo: [
                                /*
                                 * A altura da lista desconta o que a caixa gasta: o título e o
                                 * preenchimento interno. Sem isso a lista pedia mais linhas do que
                                 * a caixa cabia e as últimas saíam cortadas pelo `overflow: hidden`.
                                 */
                                linhas({
                                    alturaLinhaMm: ctx.alturaLinhaMm,
                                    comData: true,
                                    flexivel: false,
                                    alturaMm: alturaDaGradeDeDatas,
                                }),
                            ],
                            fundo: 'NENHUM',
                            borda: 'SUAVE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            alturaMm: alturaDaCaixaDeDatas,
                        }),
                    ]),
                    coluna(0.48, [
                        caixa({
                            titulo: 'Calendário do mês',
                            conteudo: [
                                miniCalendario(ano, mes, {
                                    destacar: dias,
                                    mostrarNumeroSemana: ctx.nivel !== 'MINIMO',
                                    semDatas: !opcoes.datado,
                                    feriados,
                                    mostrarNomesFeriados: Boolean(config.mostrarNomesFeriados),
                                }),
                                espaco(2),
                                texto(opcoes.datado
                                    ? `${dias.length} dias · ${Math.max(1, Math.ceil(dias.length / 7))} semanas`
                                    : 'Agenda permanente: escreva as datas você mesmo.', { tamanho: 'XS', cor: 'TEXTO_SUAVE', alinhamento: 'CENTRO' }),
                                /*
                                 * Os feriados escritos por extenso — **só quando o calendário
                                 * acima não escreveu**. Com "mostrar nomes dos feriados"
                                 * ligado, o próprio mini calendário já imprime o nome de cada
                                 * um ao lado da grade, e repetir aqui seria dizer duas vezes a
                                 * mesma coisa na mesma caixa. Desligado, os feriados ficam só
                                 * como ponto colorido: é aqui que o nome deles aparece.
                                 */
                                ...(feriados.length > 0 && !config.mostrarNomesFeriados
                                    ? [
                                        espaco(1.5),
                                        texto('Feriados do mês', {
                                            tamanho: 'XS',
                                            cor: 'DESTAQUE_TEXTO',
                                            maiusculas: true,
                                            tracking: 0.2,
                                            peso: 'FORTE',
                                            alinhamento: 'CENTRO',
                                        }),
                                        texto(feriados
                                            .slice(0, 3)
                                            .map((feriado) => `${String(feriado.dia).padStart(2, '0')} · ${feriado.nome}`)
                                            .concat(feriados.length > 3 ? [`e mais ${feriados.length - 3}`] : [])
                                            .join(' · '), { tamanho: 'XS', cor: 'TEXTO_SUAVE', alinhamento: 'CENTRO' }),
                                    ]
                                    : []),
                            ],
                            fundo: 'DESTAQUE_SUAVE',
                            borda: 'DESTAQUE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            flexivel: true,
                        }),
                    ]),
                ],
            }),
        ],
    };
}
/* ------------------------------------------------------------------ */
/* Página de dias                                                      */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* Quadros opcionais do dia                                            */
/* ------------------------------------------------------------------ */
/**
 * Altura natural aproximada de cada quadro lateral, em milímetros.
 *
 * São **mínimos**, e não só uma estimativa: o quadro é uma caixa com altura fixa
 * e `overflow: hidden`, então uma altura menor que o conteúdo corta — foi o que
 * aconteceu com a caixa de Gratidão no A6 (0,8 mm a menos que o título, o vão e
 * as três linhas pedem). Quem decide o que cabe na coluna usa esta tabela
 * (`dividirQuadrosLaterais`), então o valor certo aqui é o que evita o corte e
 * deixa o gerador avisar quando um quadro fica de fora.
 */
const ALTURA_QUADRO_MM = {
    /*
     * `CURIOSIDADE_DO_DIA` é o quadro mais alto dos conteúdos do dia: ele carrega
     * tema, fato e o porquê, e o fato costuma ocupar duas linhas mesmo em A5.
     */
    CURIOSIDADE_DO_DIA: 44,
    /* O experimento mais as duas linhas para anotar o que se viu. */
    EXPERIMENTE_HOJE: 34,
    /*
     * `PERGUNTA_DO_DIA` carrega tema, pergunta e o porquê — mesma conta do quadro
     * de curiosidade, que é o outro conteúdo de três partes.
     */
    PERGUNTA_DO_DIA: 44,
    /* O passo de escrita mais três linhas de resposta. */
    DIARIO_GUIADO: 40,
    /*
     * `DESAFIO_DO_DIA` tem a mesma estrutura da pergunta (tema, desafio e pista),
     * e o desafio costuma pedir duas linhas — por isso o mesmo número dela.
     */
    DESAFIO_DO_DIA: 44,
    /* A palavra, o significado e duas linhas para a frase. */
    PALAVRA_NOVA: 34,
    PALAVRA_DO_DIA: 32,
    FRASE_DO_DIA: 32,
    PRIORIDADES: 36,
    META_DO_DIA: 30,
    GRATIDAO: 32,
    ORACAO: 34,
    HABITOS: 44,
    CONTATOS_DO_DIA: 30,
    IDEIAS: 40,
    CALENDARIO_MES: 42,
};
/** Quadro que estica para absorver a sobra da coluna lateral. */
const QUADRO_ELASTICO = 'REVISAO_NOTURNA';
/**
 * A grade de horários também estica — e, quando está ligada, é ela que absorve a
 * sobra da coluna.
 *
 * Ela tem muitas linhas (uma por hora, ou por meia hora) e a altura da coluna é
 * o que decide o espaço de cada uma: sobra pouca altura, as horas ficam mais
 * próximas, e nada é cortado.
 */
const GRADE_ELASTICA = 'GRADE_HORARIOS';
const ALTURA_MINIMA_ELASTICO_MM = 26;
/** Reserva da coluna quando quem estica é a grade: menos que isso não desenha hora. */
const ALTURA_MINIMA_GRADE_MM = 46;
/** Qual quadro absorve a sobra da coluna lateral neste miolo. */
function quadroElastico(config) {
    return config.quadros[GRADE_ELASTICA] ? GRADE_ELASTICA : QUADRO_ELASTICO;
}
function conteudoDoDia(ctx, indice, estilo) {
    if (estilo === 'DEVOCIONAL') {
        return { devocional: DEVOCIONAIS[indice % DEVOCIONAIS.length] };
    }
    if (estilo === 'MOTIVACIONAL') {
        return { motivacional: MOTIVACIONAIS[indice % MOTIVACIONAIS.length] };
    }
    if (estilo === 'CURIOSIDADES') {
        return { curiosidade: CURIOSIDADES[indice % CURIOSIDADES.length] };
    }
    if (estilo === 'PERGUNTAS') {
        return { pergunta: PERGUNTAS[indice % PERGUNTAS.length] };
    }
    if (estilo === 'ESTUDAR') {
        return { desafio: DESAFIOS[indice % DESAFIOS.length] };
    }
    return {};
}
/** Rótulos do campo de data nas agendas permanentes, por densidade. */
const PADRAO_DATA = {
    AMPLO: '____/____/______',
    COMPACTO: '____/____/______',
    MINIMO: '__/__/____',
};
const TAMANHO_DATA = {
    AMPLO: 'LG',
    COMPACTO: 'MD',
    MINIMO: 'SM',
};
/**
 * Régua com as sete siglas de dia da semana, em caixinhas, para o usuário
 * circular a data certa em agendas que saem sem data impressa.
 */
function marcadoresDeSemana(ctx) {
    const { alturaCabecalhoDiaMm } = ctx;
    // Descontamos o preenchimento do cabeçalho e limitamos para a régua não
    // virar uma faixa exagerada em A4 com um dia por página.
    const disponivel = Math.min(Math.max(alturaCabecalhoDiaMm - 6, 6), 16);
    const comRotulo = disponivel >= 12;
    return grupo(D.SIGLAS_DIAS.map((sigla, indice) => texto(sigla, {
        tamanho: 'XS',
        peso: 'FORTE',
        alinhamento: 'CENTRO',
        cor: indice === 0 || indice === 6 ? 'DESTAQUE_TEXTO' : 'TEXTO',
    })), {
        colunas: 7,
        gapMm: 1,
        celulasVisiveis: true,
        preenchimentoCelulaMm: 0.5,
        centralizarCelulas: true,
        flexivel: false,
        alturaMm: disponivel,
        rotulo: comRotulo ? 'Marque o dia da semana' : undefined,
        rotuloCor: 'TEXTO_SUAVE',
    });
}
function blocosCabecalhoDia(ctx, dataISO, indice) {
    const { config } = ctx;
    const tamanhoNumero = config.diasPorPagina === 2 ? 'XL' : 'DISPLAY';
    const esquerda = [];
    if (dataISO) {
        const p = D.partesDe(dataISO);
        esquerda.push(texto(D.nomeDiaSemana(p.diaSemana, 'MAIUSCULO'), {
            tamanho: 'XS',
            cor: 'DESTAQUE_TEXTO',
            maiusculas: true,
            tracking: 0.16,
            peso: 'FORTE',
        }), texto(String(p.dia), {
            tamanho: tamanhoNumero,
            peso: 'FORTE',
            cor: 'TEXTO',
            entrelinha: 0.95,
        }), texto(`${D.nomeMes(p.mes)} de ${p.ano}`, {
            tamanho: 'XS',
            cor: 'TEXTO_SUAVE',
            maiusculas: true,
            tracking: 0.1,
        }));
    }
    else {
        // Agenda permanente: sem numeração de dia. Fica só o campo de data para
        // preencher e a régua de siglas para marcar o dia da semana.
        esquerda.push(texto('Data', {
            tamanho: 'XS',
            cor: 'DESTAQUE_TEXTO',
            maiusculas: true,
            tracking: 0.18,
            peso: 'FORTE',
        }), texto(PADRAO_DATA[ctx.nivel], {
            tamanho: TAMANHO_DATA[ctx.nivel],
            peso: 'FORTE',
            cor: 'TEXTO',
            entrelinha: 1.1,
        }));
    }
    const direita = [];
    if (dataISO) {
        /** Feriado marcado pelo usuário aparece no próprio dia, não só no calendário. */
        const feriado = feriadoEm(dataISO, modoDeFeriados(config), config.uf || undefined);
        if (feriado) {
            direita.push(texto(feriado.nome, {
                tamanho: 'XS',
                cor: 'DESTAQUE_TEXTO',
                alinhamento: 'DIR',
                peso: 'FORTE',
            }));
        }
        direita.push(texto(`Semana ${D.numeroSemana(dataISO)}`, {
            tamanho: 'XS',
            cor: 'TEXTO_SUAVE',
            alinhamento: 'DIR',
            maiusculas: true,
            tracking: 0.1,
        }), texto(`${D.trimestreDoMes(D.partesDe(dataISO).mes)}º trimestre`, {
            tamanho: 'XS',
            cor: 'TEXTO_SUAVE',
            alinhamento: 'DIR',
        }));
    }
    else {
        direita.push(marcadoresDeSemana(ctx));
    }
    return secao(`dia-${indice}-cabecalho`, [], {
        alturaMm: ctx.alturaCabecalhoDiaMm,
        peso: 0,
        fundo: 'SUAVE',
        raioMm: 2.5,
        preenchimentoMm: 3,
        gapMm: 3,
        alinhamentoVertical: 'CENTRO',
        colunas: [coluna(0.62, esquerda), coluna(0.38, direita)],
    });
}
/**
 * Divide os quadros laterais entre os que cabem na coluna do dia e os que não.
 *
 * A conta é a mesma usada na montagem da página e no aviso ao usuário: assim o
 * painel consegue dizer “este quadro não entrou” sem repetir a regra.
 */
function dividirQuadrosLaterais(pedidos, disponivel, fator, reserva) {
    const escolhidos = [];
    const descartados = [];
    let soma = reserva;
    for (const quadro of pedidos) {
        const altura = (ALTURA_QUADRO_MM[quadro] ?? 30) * fator;
        if (soma + altura <= disponivel) {
            soma += altura;
            escolhidos.push(quadro);
        }
        else {
            descartados.push(quadro);
        }
    }
    // Se nem o primeiro coube, mantemos ele assim mesmo: é melhor mostrar o
    // quadro principal um pouco apertado do que uma coluna vazia.
    if (escolhidos.length === 0 && descartados.length > 0) {
        escolhidos.push(descartados.shift());
    }
    return { escolhidos, descartados };
}
/** Altura que a coluna estreita guarda para a escrita, em milímetros. */
const ALTURA_MINIMA_ESCRITA_MM = 34;
/** Menor caixa que o usuário pode deixar ao arrastar (abaixo disso não cabe nada). */
const CAIXA_LIVRE_MINIMA_MM = 6;
/**
 * Reserva de altura da coluna estreita.
 *
 * Quando a **grade de horários** está ligada, ela fica com a coluna larga e a
 * escrita desce para a estreita — e aí a reserva é da escrita, que precisa de um
 * mínimo para ser útil. Nos outros casos a reserva é do quadro que estica, como
 * sempre foi.
 */
function reservaDaColuna(elastico, pedidos) {
    if (elastico === null)
        return ALTURA_MINIMA_ESCRITA_MM;
    if (!pedidos.includes(elastico))
        return 0;
    return elastico === GRADE_ELASTICA ? ALTURA_MINIMA_GRADE_MM : ALTURA_MINIMA_ELASTICO_MM;
}
/**
 * A grade de horários, quando ela fica com a coluna larga.
 *
 * Dia livre não entra: nele a grade vira linhas de anotação, que vivem bem na
 * coluna estreita como qualquer outro quadro.
 */
function gradeNaColunaLarga(ctx, dataISO) {
    if (!ctx.config.quadros[GRADE_ELASTICA])
        return null;
    if (!horasDoDia(ctx.config, dataISO))
        return null;
    return blocoDoQuadroLateral(ctx, GRADE_ELASTICA, 0, 1, dataISO);
}
/**
 * Reparte os quadros do dia entre a coluna larga e a estreita.
 *
 * A regra de sempre: a **anotação** ocupa a coluna larga e os quadros a estreita.
 * Com a **grade de horários** ligada, isso se inverte — a grade fica com a coluna
 * larga, porque é nela que se escreve o horário do compromisso e linha curta não
 * serve, e a anotação desce para a estreita, junto dos outros quadros. Como a
 * anotação é flexível, ela absorve o que sobrar da coluna.
 */
function lateraisDoDia(ctx, indiceConteudo, dataISO, exceto) {
    const pedidos = quadrosLigados(ctx.config.quadros, 'LATERAL').filter((quadro) => !exceto?.has(quadro));
    const grade = gradeNaColunaLarga(ctx, dataISO);
    const restantes = grade ? pedidos.filter((quadro) => quadro !== GRADE_ELASTICA) : pedidos;
    if (restantes.length === 0) {
        return { largos: grade ? [grade] : [], estreitos: [], descartados: [] };
    }
    const fator = ctx.nivel === 'AMPLO' ? 1 : 0.82;
    const elastico = grade ? null : quadroElastico(ctx.config);
    const fixos = restantes.filter((quadro) => quadro !== elastico);
    const { escolhidos, descartados } = dividirQuadrosLaterais(fixos, ctx.alturaEscritaMm, fator, reservaDaColuna(elastico, restantes));
    // O quadro elástico vai por último para absorver a sobra da coluna.
    if (elastico && restantes.includes(elastico))
        escolhidos.push(elastico);
    return {
        largos: grade ? [grade] : [],
        estreitos: escolhidos
            .map((quadro) => blocoDoQuadroLateral(ctx, quadro, indiceConteudo, fator, dataISO))
            .filter((bloco) => bloco !== null),
        descartados,
    };
}
/* ------------------------------------------------------------------ */
/* Ícone do quadro                                                     */
/* ------------------------------------------------------------------ */
/**
 * O quadro com o desenho do título, quando o miolo pede ícones.
 *
 * O desenho é um dos **motivos de linha** (a mesma biblioteca que enfeita a
 * pauta), escolhido por assunto em `ICONE_QUADRO`, e sai na cor de destaque do
 * tema. Duas decisões de tamanho, ambas para não mexer no que já está medido:
 *
 * - ele é desenhado com a **altura da linha do título** (o renderizador calcula),
 *   então a linha do título continua com a mesma altura e a caixa não precisa de
 *   um milímetro a mais;
 * - o título segue medido como sempre e o desenho ocupa a largura que sobra antes
 *   dele. O que isso pode custar é o título passar a duas linhas numa caixa
 *   apertada — por isso o teste de navegador mede a combinação mais apertada que
 *   existe (A6, dois dias, fonte caligráfica larga) com os ícones ligados.
 */
function comIconeDoQuadro(ctx, quadro, bloco) {
    if (!bloco || !ctx.config.iconeQuadros || bloco.tipo !== 'CAIXA')
        return bloco;
    return { ...bloco, icone: ICONE_QUADRO[quadro] };
}
/** Quadro da coluna lateral, com o desenho do título quando os ícones estão ligados. */
function blocoDoQuadroLateral(ctx, quadro, indiceConteudo, fator, dataISO, alturaFixaMm) {
    return comIconeDoQuadro(ctx, quadro, montarQuadroLateral(ctx, quadro, indiceConteudo, fator, dataISO, alturaFixaMm));
}
/** Quadro da faixa do pé da página, com o mesmo desenho. */
function blocoDoQuadroDaFaixa(ctx, quadro, indiceConteudo, alturaLinha, alturaMm = ALTURA_FAIXA_MM) {
    const bloco = montarQuadroDaFaixa(ctx, quadro, indiceConteudo, alturaLinha, alturaMm);
    return comIconeDoQuadro(ctx, quadro, bloco) ?? bloco;
}
/** Título do quadro, respeitando o rótulo personalizado do modelo. */
function tituloDoQuadro(ctx, quadro) {
    return ctx.config.rotulosQuadros[quadro]?.trim() || ROTULO_QUADRO[quadro];
}
/** Título da área de escrita principal do dia. */
function tituloDaEscrita(ctx) {
    const personalizado = ctx.config.tituloAnotacoes?.trim();
    if (personalizado)
        return personalizado;
    return ctx.config.escrita.comData ? 'Lista do dia' : 'Anotações';
}
/** Monta o quadro da coluna lateral: a caixa, o título e o conteúdo do dia. */
function montarQuadroLateral(ctx, quadro, indiceConteudo, fator, dataISO, 
/**
 * Altura escolhida à mão (caixa posicionada pelo usuário), em milímetros.
 *
 * Sem ela vale a conta de sempre: a tabela de mínimos vezes o fator da coluna,
 * e o quadro elástico abraçando a sobra.
 */
alturaFixaMm) {
    const { config } = ctx;
    const alturaLinha = ctx.alturaLinhaMm;
    const conteudo = conteudoDoDia(ctx, indiceConteudo, config.estilo);
    const alturaPedida = ALTURA_QUADRO_MM[quadro] ?? 30;
    const altura = alturaFixaMm ?? (quadro === quadroElastico(config) ? null : Math.round(alturaPedida * fator));
    const comLinha = () => linhas({
        ...enfeitesDePauta(config.escrita),
        alturaLinhaMm: alturaLinha,
        espacamentoMm: ctx.espacamentoMm,
        estilo: config.escrita.estilo,
        flexivel: true,
    });
    switch (quadro) {
        case 'PERGUNTA_DO_DIA': {
            const pergunta = conteudo.pergunta;
            if (!pergunta)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'PERGUNTA_DO_DIA'),
                conteudo: [
                    texto(pergunta.tema, { tamanho: 'SM', peso: 'FORTE', cor: 'DESTAQUE_TEXTO' }),
                    texto(pergunta.pergunta, { tamanho: 'XS', cor: 'TEXTO', espacoAcimaMm: 1 }),
                    texto(pergunta.aprofunda, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
                ],
                fundo: 'DESTAQUE_SUAVE',
                borda: 'DESTAQUE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'DIARIO_GUIADO': {
            const pergunta = conteudo.pergunta;
            if (!pergunta)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'DIARIO_GUIADO'),
                conteudo: [
                    texto(pergunta.passo, { tamanho: 'XS', cor: 'TEXTO' }),
                    /*
                     * Três linhas para responder: o quadro dá o ponto de partida, o texto
                     * fica no papel. Sem elas o passo vira só um aviso na página.
                     */
                    linhas({
                        ...enfeitesDePauta(config.escrita),
                        alturaLinhaMm: alturaLinha,
                        espacamentoMm: ctx.espacamentoMm,
                        estilo: config.escrita.estilo,
                        flexivel: true,
                        alturaMm: alturaLinha * 3,
                    }),
                ],
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'DESAFIO_DO_DIA': {
            const desafio = conteudo.desafio;
            if (!desafio)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'DESAFIO_DO_DIA'),
                conteudo: [
                    texto(desafio.tema, { tamanho: 'SM', peso: 'FORTE', cor: 'DESTAQUE_TEXTO' }),
                    texto(desafio.desafio, { tamanho: 'XS', cor: 'TEXTO', espacoAcimaMm: 1 }),
                    /*
                     * A pista ensina o caminho, e não entrega o resultado: é ela que faz o
                     * quadro valer para a criança e para quem está ajudando.
                     */
                    texto(`Pista: ${desafio.pista}`, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
                ],
                fundo: 'DESTAQUE_SUAVE',
                borda: 'DESTAQUE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'PALAVRA_NOVA': {
            const desafio = conteudo.desafio;
            if (!desafio)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'PALAVRA_NOVA'),
                conteudo: [
                    texto(desafio.palavra, { tamanho: 'SM', peso: 'FORTE', cor: 'PRINCIPAL_ESCURO' }),
                    texto(desafio.significado, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
                    /*
                     * Duas linhas para a frase: a palavra só entra na cabeça quando sai
                     * escrita por quem aprendeu. Mesmo raciocínio do diário guiado.
                     */
                    linhas({
                        ...enfeitesDePauta(config.escrita),
                        alturaLinhaMm: alturaLinha,
                        espacamentoMm: ctx.espacamentoMm,
                        estilo: config.escrita.estilo,
                        flexivel: true,
                        alturaMm: alturaLinha * 2,
                    }),
                ],
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'PALAVRA_DO_DIA': {
            const dev = conteudo.devocional;
            if (!dev)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'PALAVRA_DO_DIA'),
                conteudo: [
                    texto(dev.referencia, { tamanho: 'SM', peso: 'FORTE', cor: 'DESTAQUE_TEXTO' }),
                    texto(dev.reflexao, { tamanho: 'XS', cor: 'TEXTO', espacoAcimaMm: 1 }),
                ],
                fundo: 'DESTAQUE_SUAVE',
                borda: 'DESTAQUE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'CURIOSIDADE_DO_DIA': {
            const curiosidade = conteudo.curiosidade;
            if (!curiosidade)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'CURIOSIDADE_DO_DIA'),
                conteudo: [
                    texto(curiosidade.tema, { tamanho: 'SM', peso: 'FORTE', cor: 'DESTAQUE_TEXTO' }),
                    texto(curiosidade.fato, { tamanho: 'XS', cor: 'TEXTO', espacoAcimaMm: 1 }),
                    texto(curiosidade.detalhe, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1 }),
                ],
                fundo: 'DESTAQUE_SUAVE',
                borda: 'DESTAQUE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'EXPERIMENTE_HOJE': {
            const curiosidade = conteudo.curiosidade;
            if (!curiosidade)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'EXPERIMENTE_HOJE'),
                conteudo: [
                    texto(curiosidade.experimente, { tamanho: 'XS', cor: 'TEXTO' }),
                    /*
                     * Duas linhas para o que a pessoa viu: o quadro é o convite, e a
                     * observação fica no papel, não na tela.
                     */
                    linhas({
                        ...enfeitesDePauta(config.escrita),
                        alturaLinhaMm: alturaLinha,
                        espacamentoMm: ctx.espacamentoMm,
                        estilo: config.escrita.estilo,
                        flexivel: true,
                        alturaMm: alturaLinha * 2,
                    }),
                ],
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'FRASE_DO_DIA': {
            const mot = conteudo.motivacional;
            if (!mot)
                return null;
            return caixa({
                titulo: tituloDoQuadro(ctx, 'FRASE_DO_DIA'),
                conteudo: [
                    texto(mot.frase, { tamanho: 'SM', peso: 'FORTE', cor: 'DESTAQUE_TEXTO', entrelinha: 1.15 }),
                    texto(mot.acao, { tamanho: 'XS', cor: 'TEXTO_SUAVE', espacoAcimaMm: 1.5 }),
                ],
                fundo: 'DESTAQUE_SUAVE',
                borda: 'DESTAQUE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'PRIORIDADES':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'PRIORIDADES'),
                conteudo: [checklist(['', '', ''], { alturaLinhaMm: Math.max(6, alturaLinha), cor: 'TEXTO' })],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        case 'META_DO_DIA':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'META_DO_DIA'),
                conteudo: [
                    campos([
                        { rotulo: 'O que', linhas: 1 },
                        { rotulo: 'Como', linhas: 1 },
                    ], { alturaLinhaMm: Math.max(6, alturaLinha) }),
                ],
                fundo: 'NENHUM',
                borda: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        case 'REVISAO_NOTURNA':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'REVISAO_NOTURNA'),
                conteudo: [comLinha()],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                flexivel: true,
            });
        case 'GRATIDAO':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'GRATIDAO'),
                conteudo: [checklist(['', '', ''], { alturaLinhaMm: Math.max(6, alturaLinha), cor: 'TEXTO' })],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        case 'ORACAO':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'ORACAO'),
                conteudo: [altura === null ? comLinha() : linhas({ alturaLinhaMm: alturaLinha, flexivel: true })],
                fundo: 'NENHUM',
                borda: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        /**
         * O mês inteiro em grade, com o dia da página destacado.
         *
         * É o “calendário extra” da página de dia: mostra onde a data cai dentro do
         * mês e marca os feriados. Agenda permanente não tem mês — sem data, não há
         * calendário para mostrar.
         */
        case 'CALENDARIO_MES': {
            if (!dataISO)
                return null;
            const p = D.partesDe(dataISO);
            return caixa({
                titulo: tituloDoQuadro(ctx, 'CALENDARIO_MES'),
                conteudo: [
                    miniCalendario(p.ano, p.mes, {
                        destacar: [p.dia],
                        mostrarNumeroSemana: false,
                        feriados: feriadosDe(config, p.ano, p.mes),
                        mostrarNomesFeriados: false,
                    }),
                ],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        }
        case 'HABITOS':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'HABITOS'),
                conteudo: [checklist(['', '', '', '', ''], { alturaLinhaMm: Math.max(6, alturaLinha - 0.5), cor: 'TEXTO' })],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        /**
         * Grade de horários do dia.
         *
         * A coluna de horas acompanha o grupo de dias da página: segunda a sexta,
         * sábado e domingo têm o horário deles. O grupo marcado como livre sai só com
         * linhas — é o domingo sem compromisso, que pede espaço para escrever e não
         * uma grade de horas vazia.
         */
        case 'GRADE_HORARIOS': {
            const horas = horasDoDia(config, dataISO);
            if (!horas) {
                return caixa({
                    titulo: config.rotulosQuadros.GRADE_HORARIOS?.trim() || ROTULO_DIA_LIVRE,
                    conteudo: [comLinha()],
                    fundo: 'SUAVE',
                    raioMm: 3,
                    preenchimentoMm: 3,
                    flexivel: true,
                });
            }
            return caixa({
                titulo: tituloDoQuadro(ctx, 'GRADE_HORARIOS'),
                conteudo: [horarios(horas, { alturaLinhaMm: Math.max(5, alturaLinha) })],
                fundo: 'NENHUM',
                borda: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                flexivel: true,
            });
        }
        case 'CONTATOS_DO_DIA':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'CONTATOS_DO_DIA'),
                conteudo: [linhas({ alturaLinhaMm: Math.max(6, alturaLinha), flexivel: true })],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        case 'IDEIAS':
            return caixa({
                titulo: tituloDoQuadro(ctx, 'IDEIAS'),
                conteudo: [linhas({ alturaLinhaMm: Math.max(5.5, alturaLinha - 0.5), flexivel: true })],
                fundo: 'NENHUM',
                borda: 'TRACEJADA',
                raioMm: 3,
                preenchimentoMm: 3,
                alturaMm: altura ?? undefined,
                flexivel: altura === null,
            });
        default:
            return null;
    }
}
/** Altura da linha usada nos quadros do pé da página. */
function alturaLinhaDaFaixa(ctx) {
    return Math.max(4.5, ctx.alturaLinhaMm - 1.5);
}
/**
 * Quadros rápidos do pé da página: água, humor, tempo, gastos, aniversariantes.
 *
 * `exceto` são as caixas que o usuário posicionou à mão: elas saem da faixa (o
 * lugar delas na fila volta para quem ficou) e passam a ser desenhadas na caixa
 * escolhida.
 */
function blocosDaFaixa(ctx, indiceConteudo, exceto) {
    const pedidos = quadrosLigados(ctx.config.quadros, 'FAIXA').filter((quadro) => !exceto?.has(quadro));
    if (pedidos.length === 0)
        return [];
    const escolhidos = pedidos.slice(0, ctx.papel.quadrosNaFaixa);
    const alturaLinha = alturaLinhaDaFaixa(ctx);
    return escolhidos.map((quadro) => blocoDoQuadroDaFaixa(ctx, quadro, indiceConteudo, alturaLinha));
}
/** Um quadro do pé da página, montado. */
function montarQuadroDaFaixa(ctx, quadro, indiceConteudo, alturaLinha, alturaMm = ALTURA_FAIXA_MM) {
    const miniGrupo = (quantidade, celulas) => grupo(celulas.map((valor) => texto(valor, { tamanho: 'XS', alinhamento: 'CENTRO', cor: 'TEXTO' })), {
        colunas: quantidade,
        gapMm: 0.5,
        celulasVisiveis: true,
        preenchimentoCelulaMm: 0.2,
        centralizarCelulas: true,
        flexivel: false,
        alturaMm: 9,
    });
    {
        const base = {
            fundo: 'SUAVE',
            raioMm: 3,
            preenchimentoMm: 2.5,
            alturaMm,
        };
        switch (quadro) {
            case 'AGUA':
                return caixa({
                    titulo: tituloDoQuadro(ctx, 'AGUA'),
                    conteudo: [miniGrupo(8, Array.from({ length: 8 }, () => ''))],
                    ...base,
                });
            case 'HUMOR':
                return caixa({
                    titulo: tituloDoQuadro(ctx, 'HUMOR'),
                    conteudo: [miniGrupo(5, ['1', '2', '3', '4', '5'])],
                    ...base,
                });
            case 'TEMPO':
                return caixa({
                    titulo: tituloDoQuadro(ctx, 'TEMPO'),
                    conteudo: [
                        campos([{ rotulo: 'Mín' }, { rotulo: 'Máx' }], { alturaLinhaMm: alturaLinha }),
                    ],
                    ...base,
                });
            case 'FINANCAS':
                return caixa({
                    titulo: tituloDoQuadro(ctx, 'FINANCAS'),
                    conteudo: [campos([{ rotulo: 'Entra' }, { rotulo: 'Sai' }], { alturaLinhaMm: alturaLinha })],
                    ...base,
                });
            case 'ANIVERSARIANTES':
                return caixa({
                    titulo: tituloDoQuadro(ctx, 'ANIVERSARIANTES'),
                    conteudo: [linhas({ alturaLinhaMm: alturaLinha, flexivel: true })],
                    ...base,
                });
            default: {
                const conteudo = conteudoDoDia(ctx, indiceConteudo, ctx.config.estilo);
                const textoCurto = conteudo.devocional?.reflexao ?? conteudo.motivacional?.frase;
                return caixa({
                    titulo: 'Recado',
                    conteudo: [texto(textoCurto ?? '', { tamanho: 'XS', cor: 'TEXTO' })],
                    ...base,
                });
            }
        }
    }
}
/* ------------------------------------------------------------------ */
/* Caixas posicionadas à mão                                           */
/* ------------------------------------------------------------------ */
/**
 * A posição livre vale só quando a folha é de **um dia**.
 *
 * Com dois dias na mesma página (ou com o fim de semana dividido) a geometria é
 * repartida entre eles: mover uma caixa mexeria em dois dias de uma vez. Nesse
 * caso as posições guardadas são ignoradas — e o motor **avisa**, em vez de
 * fingir que aplicou.
 */
export function posicoesLivresPermitidas(config) {
    return config.diasPorPagina === 1 && !fimDeSemanaDividido(config);
}
/** A mesma pergunta, no meio da montagem: a folha desta página aceita posição escolhida à mão? */
function posicaoLivreVale(ctx) {
    return posicoesLivresPermitidas(ctx.config);
}
/** As chaves que saíram do fluxo: as caixas que têm posição escolhida por este miolo. */
function deslocadasDoDia(ctx) {
    if (!posicaoLivreVale(ctx))
        return new Set();
    return new Set(Object.keys(ctx.config.posicoesLivres ?? {}));
}
/**
 * Encaixa a caixa pedida dentro da área de conteúdo da folha.
 *
 * Fora da folha não existe papel: deixar passar cortaria em silêncio justamente o
 * que o cliente acabou de posicionar. Então a caixa é trazida de volta e o motor
 * conta o que fez (ver `caixaLivreEncaixada` e os avisos da montagem).
 *
 * É a mesma função que o assistente usa no arrasto e nos campos de milímetro: uma
 * regra só, para o número na tela, o contorno da caixa e o desenho no papel não
 * discordarem.
 */
export function encaixarCaixaLivre(pedida, medidas) {
    const larguraMm = Math.min(Math.max(CAIXA_LIVRE_MINIMA_MM, pedida.larguraMm), medidas.larguraConteudoMm);
    const alturaMm = Math.min(Math.max(CAIXA_LIVRE_MINIMA_MM, pedida.alturaMm), medidas.alturaConteudoMm);
    return {
        larguraMm,
        alturaMm,
        xMm: Math.min(Math.max(0, pedida.xMm), Math.max(0, medidas.larguraConteudoMm - larguraMm)),
        yMm: Math.min(Math.max(0, pedida.yMm), Math.max(0, medidas.alturaConteudoMm - alturaMm)),
        /*
         * A camada vai junto: encaixar é corrigir medida, não mandar a caixa para
         * trás. Sem esta linha, arrastar uma caixa que o cliente trouxe para a frente
         * a devolvia para o fundo.
         */
        z: pedida.z,
    };
}
/** A caixa precisou ser encaixada para caber na folha? */
export function caixaLivreEncaixada(pedida, medidas) {
    const encaixada = encaixarCaixaLivre(pedida, medidas);
    const diferente = (a, b) => Math.abs(a - b) > 0.05;
    return (diferente(encaixada.xMm, pedida.xMm) ||
        diferente(encaixada.yMm, pedida.yMm) ||
        diferente(encaixada.larguraMm, pedida.larguraMm) ||
        diferente(encaixada.alturaMm, pedida.alturaMm));
}
/**
 * Tamanho e lugar sugeridos para a caixa que o usuário acabou de pegar.
 *
 * A caixa nasce parecida com o que o motor já fazia: a área de escrita e os
 * quadros do pé tomam a largura útil da folha, e os quadros de coluna nascem com
 * a largura da coluna lateral (os mesmos 42% do desenho automático). Assim o
 * "arrastar" começa de um desenho que já era do agrado de quem olhou a prévia, em
 * vez de um retângulo qualquer.
 *
 * `quantasJaPostas` desloca cada caixa nova alguns milímetros, para ela não
 * nascer exatamente por baixo da anterior.
 */
export function caixaLivrePadrao(config, chave, quantasJaPostas = 0) {
    const medidas = medirPaginaDoMiolo(config);
    const larguraUtil = medidas.larguraConteudoMm;
    const deslocamento = 4 + 6 * quantasJaPostas;
    const deFaixa = chave !== 'ESCRITA' && GRUPO_QUADRO[chave] === 'FAIXA';
    const alturaMm = chave === 'ESCRITA'
        ? 70
        : deFaixa
            ? ALTURA_FAIXA_MM
            : chave === 'GRADE_HORARIOS'
                ? 60
                : (ALTURA_QUADRO_MM[chave] ?? 40);
    const larguraMm = chave === 'ESCRITA' || deFaixa ? larguraUtil : Math.round(larguraUtil * 0.42);
    return encaixarCaixaLivre({ xMm: deslocamento, yMm: deslocamento, larguraMm, alturaMm }, medidas);
}
/**
 * A pauta principal do dia (a "área de escrita").
 *
 * `alturaMm` só existe quando a caixa foi posicionada à mão: aí a altura é a do
 * usuário e a pauta desenha as linhas que cabem nela — sem `flexivel`, porque
 * quem manda no tamanho passou a ser a caixa, não a sobra da página.
 */
function linhasDaEscrita(ctx, opcoes = {}) {
    const { config } = ctx;
    const { alturaMm, colunas = 1 } = opcoes;
    /*
     * A lista do dia é a única pauta flexível com data: ela cresce para ocupar a
     * página inteira, então quem sabe quantas linhas desenhar é a altura do dia,
     * já calculada em `criarContexto`. Antes o desenho desistia de saber e usava
     * 80 linhas fixas: numa folha A4 sobravam mais de mil pixels de caixinhas
     * escondidas e a última saía cortada ao meio.
     */
    const passoDaPauta = ctx.alturaLinhaMm + ctx.espacamentoMm;
    const disponivel = (alturaMm ?? ctx.alturaEscritaMm) - RESERVA_ROTULO_MM;
    const linhasDeData = config.escrita.comData ? linhasQueCabem(disponivel, passoDaPauta) : undefined;
    return linhas({
        ...enfeitesDePauta(config.escrita),
        alturaLinhaMm: ctx.alturaLinhaMm,
        espacamentoMm: ctx.espacamentoMm,
        estilo: config.escrita.estilo,
        rotulo: tituloDaEscrita(ctx),
        flexivel: alturaMm === undefined,
        alturaMm,
        comData: config.escrita.comData,
        colunas,
        linhas: linhasDeData,
    });
}
/**
 * As caixas que o usuário posicionou à mão, com o bloco já montado.
 *
 * A chave é o próprio nome do quadro (ou `ESCRITA`), então desligar um quadro não
 * muda o dono da posição de outro. Quadro desligado não gera caixa: a posição
 * fica guardada na configuração, pronta para quando ele voltar.
 *
 * A ordem do desenho é explícita e estável: primeiro a camada (`z`), depois a
 * ordem de `CAIXAS_MOVEIS`. Ordem de chaves do objeto não serve — o `jsonb` do
 * banco reordena, e a caixa "trazida para a frente" voltaria para trás.
 */
function caixasLivresDoDia(ctx, dataISO, indiceConteudo) {
    const pedidas = ctx.config.posicoesLivres;
    if (!pedidas || !posicaoLivreVale(ctx))
        return [];
    const emOrdem = Object.entries(pedidas)
        .filter(([, pedida]) => Boolean(pedida))
        .sort(([a, um], [b, outro]) => {
        const camada = (um?.z ?? 0) - (outro?.z ?? 0);
        return camada !== 0 ? camada : CAIXAS_MOVEIS.indexOf(a) - CAIXAS_MOVEIS.indexOf(b);
    });
    const caixas = [];
    for (const [nome, pedida] of emOrdem) {
        if (!pedida)
            continue;
        const bloco = blocoDaCaixaLivre(ctx, nome, pedida, indiceConteudo, dataISO);
        if (!bloco)
            continue;
        caixas.push({ chave: nome, ...encaixarCaixaLivre(pedida, ctx.medidas), bloco });
    }
    return caixas;
}
/**
 * A caixa existe nesta folha?
 *
 * A área de escrita sempre existe; um quadro só existe se estiver ligado. A
 * posição de um quadro desligado continua guardada na configuração, mas não
 * gera caixa nem aviso: não há o que cortar.
 */
function caixaSaiNaFolha(ctx, chave) {
    return chave === 'ESCRITA' || Boolean(ctx.config.quadros[chave]);
}
/** Monta o bloco que vive numa caixa posicionada à mão. */
function blocoDaCaixaLivre(ctx, chave, caixa, indiceConteudo, dataISO) {
    if (chave === 'ESCRITA')
        return linhasDaEscrita(ctx, { alturaMm: caixa.alturaMm });
    if (!caixaSaiNaFolha(ctx, chave))
        return null;
    return GRUPO_QUADRO[chave] === 'LATERAL'
        ? blocoDoQuadroLateral(ctx, chave, indiceConteudo, 1, dataISO, caixa.alturaMm)
        : blocoDoQuadroDaFaixa(ctx, chave, indiceConteudo, alturaLinhaDaFaixa(ctx), caixa.alturaMm);
}
function secoesDoDia(ctx, dataISO, indice, indiceConteudo) {
    const { config } = ctx;
    const secoes = [blocosCabecalhoDia(ctx, dataISO, indice)];
    /*
     * As caixas que o usuário posicionou à mão saem do fluxo: o lugar delas na
     * coluna (ou na faixa) volta para quem ficou, e elas passam a ser desenhadas
     * na caixa escolhida — ver `caixasLivresDoDia`.
     */
    const deslocadas = deslocadasDoDia(ctx);
    const lateral = ctx.nivel === 'MINIMO'
        ? { largos: [], estreitos: [], descartados: [] }
        : lateraisDoDia(ctx, indiceConteudo, dataISO, deslocadas);
    const faixa = ctx.nivel === 'MINIMO' ? [] : blocosDaFaixa(ctx, indiceConteudo, deslocadas);
    const escritaPrincipal = deslocadas.has('ESCRITA') ? null : linhasDaEscrita(ctx);
    if (lateral.largos.length > 0) {
        /*
         * A grade de horários manda na coluna larga: é nela que se escreve o
         * compromisso, e linha curta não serve.
         *
         * Na coluna estreita, a **anotação vem primeiro** — ela toma o lugar que era
         * da grade, no topo — e os outros quadros ficam embaixo dela.
         */
        const fracaoLateral = ctx.nivel === 'AMPLO' ? 0.38 : 0.42;
        secoes.push(secao(`dia-${indice}-conteudo`, [], {
            peso: 1,
            gapMm: 4,
            colunas: [
                coluna(1 - fracaoLateral, lateral.largos),
                coluna(fracaoLateral, [...(escritaPrincipal ? [escritaPrincipal] : []), ...lateral.estreitos]),
            ],
        }));
    }
    else if (lateral.estreitos.length > 0) {
        const fracaoLateral = ctx.nivel === 'AMPLO' ? 0.38 : 0.42;
        secoes.push(secao(`dia-${indice}-conteudo`, [], {
            peso: 1,
            gapMm: 4,
            colunas: [
                coluna(1 - fracaoLateral, escritaPrincipal ? [escritaPrincipal] : []),
                coluna(fracaoLateral, lateral.estreitos),
            ],
        }));
    }
    else {
        const colunas = ctx.papel.largo && ctx.nivel === 'AMPLO' ? 2 : 1;
        secoes.push(secao(`dia-${indice}-conteudo`, escritaPrincipal ? [linhasDaEscrita(ctx, { colunas })] : [], { peso: 1 }));
    }
    if (faixa.length > 0) {
        secoes.push(secao(`dia-${indice}-faixa`, [], {
            alturaMm: ALTURA_FAIXA_MM,
            peso: 0,
            gapMm: 3,
            colunas: faixa.map((bloco) => coluna(1, [bloco])),
        }));
    }
    return secoes;
}
/* ------------------------------------------------------------------ */
/* Página de semana (os sete dias na mesma folha)                      */
/* ------------------------------------------------------------------ */
/** Segunda-feira da semana que contém a data. */
function segundaDaSemana(iso) {
    const diaSemana = D.partesDe(iso).diaSemana; // 0 = domingo
    return D.somarDias(iso, -(diaSemana === 0 ? 6 : diaSemana - 1));
}
/**
 * Semanas que cobrem o intervalo: cada uma começa na segunda e traz apenas os
 * dias permitidos pela configuração de semana que caem dentro do período.
 */
function semanasDoIntervalo(inicio, fim, modoSemana) {
    const permitidos = new Set(D.diasPermitidos(modoSemana));
    const semanas = [];
    let cursor = segundaDaSemana(inicio);
    // Teto de segurança: 3 anos de semanas.
    for (let volta = 0; volta < 160 && cursor <= fim; volta += 1) {
        const dias = D.listarDias(cursor, D.somarDias(cursor, 6))
            .filter((iso) => iso >= inicio && iso <= fim)
            .filter((iso) => permitidos.has(D.partesDe(iso).diaSemana));
        if (dias.length > 0)
            semanas.push(dias);
        cursor = D.somarDias(cursor, 7);
    }
    return semanas;
}
/** Ordem dos dias na semana: segunda primeiro, domingo por último. */
const ORDEM_DA_SEMANA = [1, 2, 3, 4, 5, 6, 0];
/** Nomes dos dias que entram na semana, respeitando a configuração de semana. */
function diasDaSemanaPermitidos(modoSemana) {
    const permitidos = new Set(D.diasPermitidos(modoSemana));
    return ORDEM_DA_SEMANA.filter((dia) => permitidos.has(dia));
}
/**
 * Sete linhas de escrita (uma por dia) empilhadas com o título de cada dia.
 * É o miolo da página semanal, usado com data e sem data.
 */
function celulasDaSemana(ctx, titulos) {
    const { config } = ctx;
    const enfeites = enfeitesDePauta(config.escrita);
    const alturaLinha = Math.max(4.2, ctx.alturaLinhaMm);
    return grupo(titulos.map((titulo) => caixa({
        titulo,
        conteudo: [
            linhas({
                ...enfeites,
                alturaLinhaMm: alturaLinha,
                espacamentoMm: Math.min(ctx.espacamentoMm, 1.5),
                estilo: config.escrita.estilo,
                flexivel: true,
            }),
        ],
        fundo: 'NENHUM',
        borda: 'SUAVE',
        raioMm: 2,
        preenchimentoMm: 2,
        tamanhoTitulo: 'XS',
        alinhamento: 'ESQ',
        flexivel: true,
    })), { colunas: 1, gapMm: 1.6, flexivel: true });
}
/** Rótulo de cada dia da semana datada: “SEG 05/01 · Feriado”. */
function titulosDaSemanaDatada(ctx, dias) {
    const modo = modoDeFeriados(ctx.config);
    return dias.map((iso) => {
        const partes = D.partesDe(iso);
        const feriado = feriadoEm(iso, modo, ctx.config.uf || undefined);
        const sigla = D.nomeDiaSemana(partes.diaSemana, 'CURTO').toUpperCase().replace('.', '');
        return `${sigla} ${String(partes.dia).padStart(2, '0')}/${String(partes.mes).padStart(2, '0')}${feriado ? ` · ${feriado.nome}` : ''}`;
    });
}
/**
 * Uma página por semana: de segunda a domingo, cada dia com o seu espaço de
 * escrita. É o formato “semana” dos planejadores — bom para quem escreve pouco
 * por dia e quer o panorama dos sete dias na frente.
 */
function construirPaginaSemana(ctx, dias, numero) {
    const { config } = ctx;
    const primeiro = dias[0];
    const ultimo = dias[dias.length - 1];
    const rotulo = dias.length === 1 ? D.formatarDataLonga(primeiro) : `${D.formatarDataCurta(primeiro)} a ${D.formatarDataCurta(ultimo)}`;
    const tituloSemana = `Semana ${String(numero).padStart(2, '0')}`;
    return {
        id: `semana-${primeiro}`,
        formato: config.formato,
        tipo: 'DIA',
        titulo: `${tituloSemana} · ${rotulo}`,
        meta: {
            dataISO: primeiro,
            numeroSemana: D.numeroSemana(primeiro),
            indiceConteudo: numero - 1,
        },
        indice: `${tituloSemana} · ${rotulo}`,
        cabecalho: cabecalhoDe(ctx, config.nome, `${D.numeroSemana(primeiro)}ª semana`),
        rodape: {
            esquerda: config.nome.slice(0, 46),
            direita: `${D.formatarDataCurta(primeiro)} – ${D.formatarDataCurta(ultimo)}`,
            mostrarRegua: false,
        },
        fundo: { tipo: 'COR', temaEscuro: false },
        secoes: [
            secaoTitulo(tituloSemana, `${rotulo} · ${D.numeroSemana(primeiro)}ª semana do ano`),
            secao('semana', [], {
                peso: 1,
                colunas: [coluna(1, [celulasDaSemana(ctx, titulosDaSemanaDatada(ctx, dias))])],
            }),
        ],
    };
}
/**
 * Semana da agenda permanente: sem data nenhuma, só o nome de cada dia e o
 * espaço de escrita — a pessoa escreve a data.
 */
function construirPaginaSemanaSemData(ctx, numero) {
    const { config } = ctx;
    const titulo = `Semana ${String(numero).padStart(2, '0')}`;
    const titulos = diasDaSemanaPermitidos(config.semana).map((dia) => D.nomeDiaSemana(dia, 'LONGO').toUpperCase());
    return {
        id: `semana-sem-data-${numero}`,
        formato: config.formato,
        tipo: 'DIA',
        titulo: `${titulo} · sem data`,
        meta: { indiceConteudo: numero - 1 },
        indice: titulo,
        cabecalho: cabecalhoDe(ctx, config.nome, titulo),
        rodape: {
            esquerda: config.nome.slice(0, 46),
            direita: 'Escreva a data de cada dia',
            mostrarRegua: false,
        },
        fundo: { tipo: 'COR', temaEscuro: false },
        secoes: [
            secaoTitulo(titulo, 'Agenda permanente: escreva a data em cada dia.'),
            secao('semana', [], {
                peso: 1,
                colunas: [coluna(1, [celulasDaSemana(ctx, titulos)])],
            }),
        ],
    };
}
/**
 * O miolo usa o fim de semana dividido?
 *
 * Só vale com **um dia por página**: com dois dias por página o sábado e o
 * domingo já saem juntos, e no formato semanal os sete dias já dividem a folha.
 * Ausente na configuração (miolo salvo antes deste campo) é o mesmo que
 * desligado.
 */
export function fimDeSemanaDividido(config) {
    return config.paginasDeDia === 'DIA' && config.diasPorPagina === 1 && config.fimDeSemanaDividido === true;
}
/**
 * Reparte os dias do período nas folhas.
 *
 * Sem o fim de semana dividido é o de sempre: um ou dois dias por folha, na
 * ordem do calendário. Com ele, o sábado leva o domingo junto e todos os outros
 * dias ficam sozinhos.
 */
function folhasDeDias(dias, config) {
    if (!fimDeSemanaDividido(config)) {
        const lotes = [];
        for (let i = 0; i < dias.length; i += config.diasPorPagina) {
            lotes.push(dias.slice(i, i + config.diasPorPagina));
        }
        return lotes;
    }
    const lotes = [];
    for (let i = 0; i < dias.length; i += 1) {
        const iso = dias[i];
        const seguinte = dias[i + 1];
        /*
         * Só junta quando o domingo é o dia seguinte de verdade. Assim a regra
         * sobrevive a um mês recortado — a lista do mês começa e termina onde o
         * período mandou — e a uma agenda que não imprime domingo.
         */
        const fechamAJuntos = D.partesDe(iso).diaSemana === 6 &&
            seguinte !== undefined &&
            D.partesDe(seguinte).diaSemana === 0 &&
            D.somarDias(iso, 1) === seguinte;
        if (fechamAJuntos) {
            lotes.push([iso, seguinte]);
            i += 1;
        }
        else {
            lotes.push([iso]);
        }
    }
    return lotes;
}
function construirPaginaDias(ctx, itens) {
    const { config } = ctx;
    const secoes = [];
    itens.forEach((item, i) => {
        if (i > 0)
            secoes.push(secao(`dia-separador-${i}`, [], { alturaMm: GAP_DIAS_MM, peso: 0 }));
        secoes.push(...secoesDoDia(ctx, item.dataISO, i, item.indiceConteudo));
    });
    const primeiro = itens[0];
    const titulo = primeiro.dataISO
        ? itens
            .map((it) => (it.dataISO ? D.formatarDataCurta(it.dataISO) : ''))
            .filter(Boolean)
            .join(' e ')
        : `Sem data ${itens.map((it) => it.indiceConteudo + 1).join(' e ')}`;
    return {
        id: `dias-${primeiro.dataISO ?? primeiro.indiceConteudo}`,
        formato: config.formato,
        tipo: 'DIA',
        titulo,
        meta: {
            dataISO: primeiro.dataISO ?? undefined,
            indiceConteudo: primeiro.indiceConteudo,
            numeroSemana: primeiro.dataISO ? D.numeroSemana(primeiro.dataISO) : undefined,
        },
        rodape: rodapeDe(ctx),
        fundo: {
            tipo: 'COR',
            temaEscuro: false,
        },
        secoes,
        /* Caixas que o usuário posicionou à mão (ausente = nenhuma). */
        caixasLivres: caixasLivresDoDia(ctx, primeiro.dataISO, primeiro.indiceConteudo),
    };
}
/* ------------------------------------------------------------------ */
/* Páginas extras                                                      */
/* ------------------------------------------------------------------ */
function construirAniversarios(ctx) {
    const alturaLinha = 8;
    const disponivel = ctx.alturaEscritaMm - 30;
    const linhasTabela = Math.max(12, Math.floor(disponivel / alturaLinha));
    return {
        id: 'aniversarios',
        formato: ctx.config.formato,
        tipo: 'ANIVERSARIOS',
        titulo: 'Aniversários',
        meta: {},
        indice: 'Aniversários e datas especiais',
        cabecalho: cabecalhoDe(ctx, ctx.config.nome, 'Datas especiais'),
        rodape: rodapeDe(ctx),
        fundo: fundoClaro(),
        secoes: [
            secaoTitulo('Aniversários', 'Quem você não quer esquecer de parabenizar.'),
            secao('aniversarios-tabela', [
                tabela(['Nome', 'Dia', 'Mês', 'Idade', 'Presente / ideia'], {
                    larguras: [0.36, 0.1, 0.14, 0.1, 0.3],
                    linhas: linhasTabela,
                    alturaLinhaMm: alturaLinha,
                    flexivel: true,
                }),
            ], { peso: 1 }),
        ],
    };
}
function construirMetas(ctx) {
    const alturaLinha = Math.max(6, ctx.alturaLinhaMm);
    const habitos = [
        'Beber água',
        'Exercício',
        'Leitura',
        'Sono 8h',
        'Sem tela à noite',
        'Estudo',
    ];
    return {
        id: 'metas',
        formato: ctx.config.formato,
        tipo: 'METAS',
        titulo: 'Metas do período',
        meta: {},
        indice: 'Metas do período',
        cabecalho: cabecalhoDe(ctx, ctx.config.nome, 'Metas e hábitos'),
        rodape: rodapeDe(ctx),
        fundo: fundoClaro(),
        secoes: [
            secaoTitulo('Metas do período', 'Escreva poucas metas. Poucas, mas claras.'),
            secao('metas-corpo', [], {
                peso: 1,
                gapMm: 4,
                colunas: [
                    coluna(0.52, [
                        caixa({
                            titulo: 'Minhas metas',
                            conteudo: [
                                campos([
                                    { rotulo: 'Meta', linhas: 1 },
                                    { rotulo: 'Prazo', linhas: 1 },
                                    { rotulo: 'Primeiro passo', linhas: 1 },
                                ], { alturaLinhaMm: alturaLinha }),
                                separador({ estilo: 'TRACEJADA', cor: 'LINHA', espacoMm: 2 }),
                                campos([
                                    { rotulo: 'Meta', linhas: 1 },
                                    { rotulo: 'Prazo', linhas: 1 },
                                    { rotulo: 'Primeiro passo', linhas: 1 },
                                ], { alturaLinhaMm: alturaLinha }),
                                separador({ estilo: 'TRACEJADA', cor: 'LINHA', espacoMm: 2 }),
                                campos([
                                    { rotulo: 'Meta', linhas: 1 },
                                    { rotulo: 'Prazo', linhas: 1 },
                                    { rotulo: 'Primeiro passo', linhas: 1 },
                                ], { alturaLinhaMm: alturaLinha }),
                            ],
                            fundo: 'SUAVE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            flexivel: true,
                        }),
                    ]),
                    coluna(0.48, [
                        caixa({
                            titulo: 'Rastreador de hábitos',
                            conteudo: [
                                tabela(['Hábito', 'S', 'T', 'Q', 'Q', 'S', 'S', 'D'], {
                                    larguras: [0.36, 0.08, 0.08, 0.08, 0.08, 0.08, 0.08, 0.08],
                                    linhas: habitos.length,
                                    alturaLinhaMm: 8,
                                    flexivel: false,
                                    alturaMm: habitos.length * 8 + 8,
                                    fundo: 'NENHUM',
                                    borda: 'SUAVE',
                                }),
                            ],
                            fundo: 'SUAVE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            alturaMm: habitos.length * 8 + 34,
                        }),
                        caixa({
                            titulo: 'Revisão semanal',
                            conteudo: [linhas({ alturaLinhaMm: alturaLinha, flexivel: true })],
                            fundo: 'NENHUM',
                            borda: 'SUAVE',
                            raioMm: 3,
                            preenchimentoMm: 4,
                            flexivel: true,
                        }),
                    ]),
                ],
            }),
        ],
    };
}
/**
 * Quantas entradas do índice cabem em uma página.
 *
 * A conta é usada em dois lugares (para montar as páginas e para numerar o
 * corpo), então vive aqui: se as duas contas divergirem, a numeração do miolo
 * inteiro sai errada.
 */
function entradasPorPaginaDeIndice(ctx) {
    const alturaLinha = Math.max(6, ctx.alturaLinhaMm) + 1;
    const util = alturaUtilDoMiolo(ctx);
    // Desconta o título da seção, a moldura da caixa e uma folga de respiro.
    const disponivel = util - TITULO_SECAO_MM - 10 - 8;
    return Math.max(8, Math.floor(disponivel / alturaLinha));
}
/**
 * Índice do miolo, em quantas páginas forem necessárias.
 *
 * Antes tudo entrava em uma caixa com altura calculada: com muitos cadernos a
 * lista passava da folha e o fim do índice era cortado pela margem, sem aviso.
 */
function construirIndice(ctx, entradas) {
    const alturaLinha = Math.max(6, ctx.alturaLinhaMm) + 1;
    const porPagina = entradasPorPaginaDeIndice(ctx);
    const total = Math.max(1, Math.ceil(entradas.length / porPagina));
    const disponivel = alturaUtilDoMiolo(ctx);
    const paginas = [];
    for (let i = 0; i < total; i += 1) {
        const recorte = entradas.slice(i * porPagina, (i + 1) * porPagina);
        const alturaDaLista = recorte.length * alturaLinha + 10;
        // Só sobra espaço para escrever quando a lista já coube com folga.
        const sobra = disponivel - TITULO_SECAO_MM - alturaDaLista - 27;
        const blocos = [
            caixa({
                conteudo: [
                    campos(recorte.map((e) => ({ rotulo: e.rotulo, valor: String(e.pagina) })), { alturaLinhaMm: alturaLinha }),
                ],
                fundo: 'SUAVE',
                raioMm: 3,
                preenchimentoMm: 5,
                flexivel: false,
                alturaMm: Math.max(30, alturaDaLista),
            }),
        ];
        if (sobra > 14) {
            /*
             * As linhas de continuação são as que cabem na sobra, descontado o rótulo.
             *
             * Antes iam as `porPagina` linhas da lista: numa sobra de 147 mm cabiam 17
             * linhas de 8 mm, mas iam 25 — e as oito que passavam saíam cortadas pelo
             * `overflow: hidden`. É a mesma conta que dimensiona a caixa, para o número
             * de linhas e o espaço nunca discordarem.
             */
            const linhasDeSobra = Math.max(1, Math.floor((sobra - 8) / alturaLinha));
            blocos.push(rotulo('Continuação'), linhas({
                alturaLinhaMm: alturaLinha,
                flexivel: true,
                comData: true,
                linhas: linhasDeSobra,
            }));
        }
        paginas.push({
            id: total > 1 ? `indice-${i + 1}` : 'indice',
            formato: ctx.config.formato,
            tipo: 'INDICE',
            titulo: 'Índice',
            meta: {},
            indice: i === 0 ? 'Índice' : undefined,
            cabecalho: cabecalhoDe(ctx, ctx.config.nome, 'Sumário'),
            rodape: rodapeDe(ctx),
            fundo: fundoClaro(),
            secoes: [
                secaoTitulo(i === 0 ? 'Índice' : 'Índice (continuação)', i === 0
                    ? 'Encontre cada parte do miolo em segundos.'
                    : 'As partes que continuam do sumário.'),
                secao('indice-lista', blocos, { peso: 1 }),
            ],
        });
    }
    return paginas;
}
/* ------------------------------------------------------------------ */
/* Montagem final                                                      */
/* ------------------------------------------------------------------ */
function montarAvisos(ctx, totalDias) {
    const avisos = [];
    const { config } = ctx;
    /*
     * Posição livre das caixas: o motor conta o que fez com o que o usuário pediu.
     *
     * São três recados possíveis, e os três existem para o ajuste não virar
     * surpresa no papel: a caixa foi trazida de volta para dentro da folha; ela
     * ficou menor do que o conteúdo que carrega; ou a posição não vale neste miolo
     * (dois dias na folha).
     */
    const pedidas = Object.entries(config.posicoesLivres ?? {});
    for (const [chave, pedida] of pedidas) {
        if (!pedida)
            continue;
        const rotulo = ROTULO_CAIXA_MOVEL[chave];
        if (!posicaoLivreVale(ctx)) {
            avisos.push({
                nivel: 'ATENCAO',
                mensagem: `A posição da caixa “${rotulo}” foi guardada, mas só vale com um dia por página: com dois dias na folha (ou o fim de semana dividido) a página é repartida entre eles. Aqui ela foi ignorada.`,
            });
            continue;
        }
        /* Quadro desligado não desenha caixa: nenhum dos recados abaixo faz sentido. */
        if (!caixaSaiNaFolha(ctx, chave))
            continue;
        if (caixaLivreEncaixada(pedida, ctx.medidas)) {
            avisos.push({
                nivel: 'ATENCAO',
                mensagem: `A caixa “${rotulo}” passava da área de conteúdo da folha e foi encaixada dentro dela. Confira a posição na prévia — o que fica fora da folha não tem papel para sair.`,
            });
        }
        if (chave !== 'ESCRITA') {
            const natural = ALTURA_QUADRO_MM[chave];
            if (natural !== undefined && pedida.alturaMm < natural - 0.5) {
                avisos.push({
                    nivel: 'ATENCAO',
                    mensagem: `A caixa “${rotulo}” ficou com ${Math.round(pedida.alturaMm)} mm de altura, e o conteúdo dela pede cerca de ${natural} mm: o que passar é cortado.`,
                });
            }
        }
    }
    if (config.paginasDeDia === 'SEMANA') {
        avisos.push({
            nivel: 'INFO',
            mensagem: `Formato semanal: cada dia fica com cerca de ${Math.round(ctx.alturaEscritaMm)} mm de escrita, com os sete dias na mesma folha.`,
        });
    }
    if (ctx.alturaEscritaMm < 55 && config.paginasDeDia === 'DIA') {
        avisos.push({
            nivel: 'ATENCAO',
            mensagem: `Com ${config.formato} e ${config.diasPorPagina} dia(s) por página, cada dia fica com cerca de ${Math.round(ctx.alturaEscritaMm)} mm de área útil. Pouco espaço para escrever — considere 1 dia por página ou um formato maior.`,
        });
    }
    if (config.paginasDeDia === 'NENHUM') {
        const temConteudo = config.cadernos.length > 0 ||
            config.paginasExtras.some((extra) => extra === 'ANIVERSARIOS' || extra === 'METAS' || extra === 'DADOS_PESSOAIS');
        avisos.push({
            nivel: temConteudo ? 'INFO' : 'ATENCAO',
            mensagem: temConteudo
                ? 'Miolo sem páginas de dia: ele fica com as páginas extras e os cadernos que você escolheu.'
                : 'Miolo sem páginas de dia e sem cadernos: vai sair só com a capa e o índice. Ligue um caderno (folhas pautadas, por exemplo) para ter o que escrever.',
        });
    }
    if (config.diasPorPagina === 2 && ctx.alturaEscritaMm < 52 && config.estilo !== 'SEM_ESTILO') {
        avisos.push({
            nivel: 'ATENCAO',
            mensagem: `${config.formato} com 2 dias por página é um dos cenários mais apertados: o conteúdo do estilo (versículo/frase) entra apenas como uma faixa compacta.`,
        });
    }
    if (ctx.alturaLinhaMm < 5 && config.paginasDeDia === 'DIA') {
        avisos.push({
            nivel: 'INFO',
            mensagem: `A pauta foi reduzida para ${ctx.alturaLinhaMm} mm para caber mais linhas. Se sua letra é grande, escolha um formato maior.`,
        });
    }
    const contraste = ctx.paleta.contrasteTexto;
    if (contraste < 7) {
        avisos.push({
            nivel: 'INFO',
            mensagem: `O contraste do texto sobre o papel está em ${contraste}:1. Ative "contraste alto" nas cores para chegar a 7:1 (recomendação para impressão).`,
        });
    }
    if (config.camadasImagem.length > 0 || config.fundos.length > 0) {
        const visiveis = [
            ...config.camadasImagem,
            ...camadasDosFundos(config),
        ].filter((camada) => camada.visivel);
        const sobreEscrita = visiveis.filter((camada) => camada.escopo === 'CONTEUDO' || camada.escopo === 'TODAS');
        const arriscadas = sobreEscrita.filter((camada) => camada.opacidade > 0.4 && camada.veu < 0.3);
        if (arriscadas.length > 0) {
            avisos.push({
                nivel: 'ATENCAO',
                mensagem: `${arriscadas.length} camada(s) ficam sobre as páginas de escrita com opacidade acima de 40% e pouco véu. A escrita pode perder legibilidade no papel — reduza a opacidade ou aumente o véu.`,
            });
        }
        if (sobreEscrita.length > 0) {
            avisos.push({
                nivel: 'INFO',
                mensagem: `${sobreEscrita.length} de ${visiveis.length} camada(s) de imagem aparecem nas páginas de conteúdo. Confira a prova de impressão antes de mandar para a gráfica.`,
            });
        }
    }
    if (config.calendario.modo === 'DATADO') {
        const total = totalDias;
        if (total > 500) {
            avisos.push({
                nivel: 'INFO',
                mensagem: `Serão geradas ${total} páginas de dia. O arquivo de impressão fica grande — prefira gerar por trimestre.`,
            });
        }
    }
    else if (config.calendario.paginas > MAX_PAGINAS_DIA) {
        avisos.push({
            nivel: 'ATENCAO',
            mensagem: `Limitamos a agenda permanente a ${MAX_PAGINAS_DIA} páginas de dia para o arquivo não travar o navegador.`,
        });
    }
    if (!config.dadosPessoais.nome) {
        avisos.push({
            nivel: 'INFO',
            mensagem: 'Você não informou seu nome. A capa e a página "Meus dados" ficam sem identificação.',
        });
    }
    const lateraisPedidos = quadrosLigados(config.quadros, 'LATERAL');
    if (ctx.nivel === 'MINIMO' && lateraisPedidos.length > 0) {
        avisos.push({
            nivel: 'ATENCAO',
            mensagem: `Com ${config.formato} e ${descricaoDoModoDeDia(config)} não sobra espaço para os quadros laterais: eles foram omitidos. Use 1 dia por página ou um formato maior para vê-los.`,
        });
    }
    else if (lateraisPedidos.length > 0) {
        /**
         * A coluna lateral tem altura limitada: quando o usuário marca mais quadros
         * do que cabem, os últimos são omitidos. Sem este aviso o quadro marcado
         * simplesmente desaparece e parece que não funcionou.
         */
        const descartados = quadrosLateraisDescartados(config);
        if (descartados.length > 0) {
            const nomes = descartados.map((quadro) => ROTULO_QUADRO[quadro]).join(', ');
            avisos.push({
                nivel: 'ATENCAO',
                mensagem: `Sem espaço na coluna lateral de ${config.formato} com ${descricaoDoModoDeDia(config)}: ${nomes} ficou de fora. Desligue um quadro no passo "Quadros do dia" ou use um formato maior.`,
            });
        }
    }
    const faixaPedida = quadrosLigados(config.quadros, 'FAIXA').length;
    const faixaSuportada = PAPEIS[config.formato].quadrosNaFaixa;
    if (faixaPedida > faixaSuportada) {
        avisos.push({
            nivel: 'INFO',
            mensagem: `Você marcou ${faixaPedida} quadros rápidos, mas em ${config.formato} cabem ${faixaSuportada}. Os outros saíram da faixa inferior.`,
        });
    }
    const cadernosInvisiveis = config.cadernos.filter((caderno) => !caderno.visivel).length;
    if (cadernosInvisiveis > 0) {
        avisos.push({
            nivel: 'INFO',
            mensagem: `${cadernosInvisiveis} caderno(s) estão desativados e não entram na impressão.`,
        });
    }
    return avisos;
}
/**
 * Quadros laterais que o usuário marcou mas não cabem na coluna do dia.
 *
 * Usado pelo aviso do próprio motor e pelo painel, para explicar por que um
 * quadro marcado não aparece na página.
 */
export function quadrosLateraisDescartados(config) {
    const ctx = criarContexto(config);
    const pedidos = quadrosLigados(config.quadros, 'LATERAL');
    if (ctx.nivel === 'MINIMO' || pedidos.length === 0)
        return [];
    /*
     * A repartição é a mesma da montagem da página, com a data de hoje vazia (que
     * vale segunda a sexta). O aviso é do miolo inteiro: num miolo em que só o
     * domingo é livre, alguma página vai caber mais — e errar para mais é melhor
     * do que prometer espaço que não existe.
     */
    const grade = gradeNaColunaLarga(ctx, null);
    const restantes = grade ? pedidos.filter((quadro) => quadro !== GRADE_ELASTICA) : pedidos;
    if (restantes.length === 0)
        return [];
    const fator = ctx.nivel === 'AMPLO' ? 1 : 0.82;
    const elastico = grade ? null : quadroElastico(config);
    const fixos = restantes.filter((quadro) => quadro !== elastico);
    return dividirQuadrosLaterais(fixos, ctx.alturaEscritaMm, fator, reservaDaColuna(elastico, restantes))
        .descartados;
}
/**
 * Acabamento dos quadros do dia conforme o estilo visual.
 *
 * Os modelos desenham cada quadro como uma caixa com contorno e fundo. Aqui o
 * estilo decide o canto e o preenchimento, e a opção `quadrosComCaixa` desliga a
 * caixa inteira — é o "só os dias": some o contorno, some o fundo e o quadro
 * vira título + linhas soltas na folha.
 *
 * A grade de hábitos também perde as células desenhadas: quadradinho por
 * quadradinho é caixa, e a ideia é a folha sem moldura nenhuma.
 *
 * Tabela fica de fora: traço em tabela é o que separa uma coluna da outra, não
 * enfeite.
 */
function comAcabamentoDoEstilo(pagina, config) {
    if (pagina.tipo !== 'DIA')
        return pagina;
    const acabamento = ACABAMENTO_DO_ESTILO_VISUAL[config.estiloVisual];
    const semCaixa = !config.quadrosComCaixa || acabamento.semCaixa;
    const vestir = (bloco) => {
        if (bloco.tipo === 'GRUPO') {
            return {
                ...bloco,
                blocos: bloco.blocos.map(vestir),
                celulasVisiveis: semCaixa ? false : bloco.celulasVisiveis,
            };
        }
        if (bloco.tipo !== 'CAIXA')
            return bloco;
        const conteudo = bloco.conteudo?.map(vestir);
        if (semCaixa) {
            return {
                ...bloco,
                conteudo,
                raioMm: 0,
                preenchimentoMm: 0,
                borda: 'NENHUMA',
                fundo: 'NENHUM',
            };
        }
        return {
            ...bloco,
            conteudo,
            raioMm: acabamento.raioMm,
            fundo: acabamento.semFundo ? 'NENHUM' : bloco.fundo,
        };
    };
    return {
        ...pagina,
        secoes: pagina.secoes.map((secao) => ({
            ...secao,
            blocos: secao.blocos?.map(vestir),
            colunas: secao.colunas?.map((coluna) => ({
                ...coluna,
                blocos: coluna.blocos.map(vestir),
            })),
        })),
    };
}
export function construirDocumento(config) {
    const ctx = criarContexto(config);
    /*
     * Com o fim de semana dividido convivem duas medidas na mesma agenda: o dia de
     * semana ocupa a folha inteira, e a folha do sábado com o domingo vale metade
     * para cada um. Por isso a folha dupla é montada com o contexto de dois dias
     * por página — a mesma conta que já existe para quem escolhe 2 dias por
     * página. Sem ela, o dia de semana sairia espremido na altura do fim de
     * semana.
     */
    const ctxDaFolhaDupla = fimDeSemanaDividido(config)
        ? criarContexto({ ...config, diasPorPagina: 2 })
        : null;
    const extras = new Set(config.paginasExtras);
    /*
     * --- pré-matéria ---
     *
     * A abertura da agenda é sempre a mesma, nesta ordem: capa, folha de rosto
     * (os dados de quem usa) e os calendários — a visão do ano e o calendário de
     * aniversariantes. Quem folheia precisa achar as datas antes de chegar nos
     * dias, e não no fim do caderno.
     */
    /*
     * Sem capa por padrão; com capa quando existe **arte de capa**.
     *
     * Quem manda imprimir o miolo faz a capa por fora, então o desenho do sistema
     * ficou desligado por padrão (decisão de 27/09/2026) e só quem pede recebe a
     * capa gerada. A arte enviada pelo cliente, porém, **sempre** sai: guardar a
     * imagem e não imprimir a página seria perder o trabalho dele.
     */
    const preMateria = config.capa || config.capas?.frente ? [construirCapa(ctx)] : [];
    if (extras.has('DADOS_PESSOAIS'))
        preMateria.push(construirDadosPessoais(ctx));
    if (extras.has('ANO'))
        preMateria.push(...construirAno(ctx));
    if (extras.has('ANIVERSARIOS'))
        preMateria.push(construirAniversarios(ctx));
    /* --- corpo --- */
    const corpo = [];
    let totalDias = 0;
    let cursor = 0;
    const assinatura = assinaturaDoTema(ctx);
    if (config.calendario.modo === 'DATADO') {
        const inicio = config.calendario.inicio <= config.calendario.fim ? config.calendario.inicio : config.calendario.fim;
        const fim = config.calendario.inicio <= config.calendario.fim ? config.calendario.fim : config.calendario.inicio;
        const permitidos = new Set(D.diasPermitidos(config.semana));
        const meses = D.mesesDoIntervalo(inicio, fim);
        for (const { ano, mes } of meses) {
            const doMes = D.listarDias(D.montarIso(ano, mes, 1), D.montarIso(ano, mes, D.diasNoMes(ano, mes)))
                .filter((iso) => D.estaNoIntervalo(iso, inicio, fim))
                .filter((iso) => permitidos.has(D.partesDe(iso).diaSemana));
            if (doMes.length === 0)
                continue;
            if (extras.has('MESES')) {
                corpo.push(construirDivisoriaMes(ctx, ano, mes, {
                    datado: true,
                    destacar: doMes.map((iso) => D.partesDe(iso).dia),
                    assinatura,
                }));
            }
            if (totalDias >= MAX_PAGINAS_DIA)
                continue;
            const restante = MAX_PAGINAS_DIA - totalDias;
            const dias = doMes.slice(0, restante);
            totalDias += dias.length;
            if (config.paginasDeDia === 'NENHUM')
                continue;
            if (config.paginasDeDia === 'SEMANA') {
                // As semanas são recortadas mês a mês para ficarem junto do divisor.
                const semanas = semanasDoIntervalo(D.montarIso(ano, mes, 1) > inicio ? D.montarIso(ano, mes, 1) : inicio, D.montarIso(ano, mes, D.diasNoMes(ano, mes)) < fim ? D.montarIso(ano, mes, D.diasNoMes(ano, mes)) : fim, config.semana);
                for (const semana of semanas) {
                    corpo.push(construirPaginaSemana(ctx, semana, ++cursor));
                }
                continue;
            }
            for (const lote of folhasDeDias(dias, config)) {
                corpo.push(construirPaginaDias(
                // A folha do sábado com o domingo é a única que divide a altura.
                ctxDaFolhaDupla && lote.length > 1 ? ctxDaFolhaDupla : ctx, lote.map((iso, posicao) => ({
                    dataISO: iso,
                    // Cada dia do lote recebe um índice de conteúdo distinto, senão
                    // dois dias na mesma página mostrariam o mesmo versículo.
                    indiceConteudo: cursor + posicao,
                }))));
                cursor += lote.length;
            }
        }
    }
    else {
        const paginas = Math.min(config.calendario.paginas, MAX_PAGINAS_DIA);
        totalDias = paginas;
        if (extras.has('MESES')) {
            const anoBase = config.calendario.ano;
            for (let mes = 1; mes <= 12; mes += 1) {
                corpo.push(construirDivisoriaMes(ctx, anoBase, mes, { datado: false, assinatura }));
            }
        }
        if (config.paginasDeDia === 'NENHUM') {
            // Só as páginas extras e os cadernos: nenhuma página de dia.
        }
        else if (config.paginasDeDia === 'SEMANA') {
            for (let semana = 1; semana <= paginas; semana += 1) {
                corpo.push(construirPaginaSemanaSemData(ctx, semana));
            }
        }
        else {
            for (let i = 0; i < paginas; i += config.diasPorPagina) {
                const quantidade = Math.min(config.diasPorPagina, paginas - i);
                const lote = Array.from({ length: quantidade }, (_, k) => ({
                    dataISO: null,
                    indiceConteudo: cursor + k,
                }));
                cursor += quantidade;
                corpo.push(construirPaginaDias(ctx, lote));
            }
        }
    }
    /*
     * --- pós-matéria ---
     *
     * Fecha a parte planejada: as metas, os cadernos e a contracapa. Os
     * calendários não entram aqui — eles abrem o miolo, na pré-matéria.
     */
    const posMateria = [];
    if (extras.has('METAS'))
        posMateria.push(construirMetas(ctx));
    // Cadernos extras: contatos, senhas, orações, caligrafia, partitura, desenho...
    const contextoCaderno = {
        formato: config.formato,
        papel: ctx.papel,
        medidas: ctx.medidas,
        alturaLinhaMm: ctx.alturaLinhaMm,
        espacamentoMm: ctx.espacamentoMm,
        escrita: config.escrita,
        nomeDoMiolo: config.nome,
    };
    for (const caderno of config.cadernos) {
        posMateria.push(...construirPaginasDoCaderno(contextoCaderno, caderno));
    }
    /* A contracapa fecha o miolo: arte enviada ou página em branco. */
    if (config.capas?.incluirContracapa || config.capas?.verso) {
        posMateria.push(construirContracapa(ctx));
    }
    /*
     * O acabamento do estilo visual entra de uma vez, aqui, em todas as páginas de
     * dia — em vez de espalhar a decisão por cada construtor de quadro. É o último
     * passo do desenho antes da numeração.
     */
    for (let i = 0; i < corpo.length; i += 1) {
        corpo[i] = comAcabamentoDoEstilo(corpo[i], config);
    }
    /* --- numeração (o índice é inserido depois da pré-matéria) --- */
    const incluirIndice = extras.has('INDICE');
    /*
     * O índice pode ocupar mais de uma página: um miolo com muitos cadernos tem
     * muitas entradas. Contamos quantas páginas ele vai ocupar ANTES de numerar,
     * senão o corpo inteiro começa com o número errado.
     */
    const quantasEntradasNoIndice = [...preMateria, ...corpo, ...posMateria].filter((pagina) => pagina.indice).length;
    const paginasDoIndice = incluirIndice
        ? Math.max(1, Math.ceil(quantasEntradasNoIndice / entradasPorPaginaDeIndice(ctx)))
        : 0;
    const numeroInicialCorpo = preMateria.length + paginasDoIndice + 1;
    const comNumeros = [];
    let numero = 1;
    for (const pagina of preMateria) {
        pagina.meta.numeroPagina = numero;
        if (pagina.rodape)
            pagina.rodape.centro = config.numerarPaginas ? String(numero) : undefined;
        comNumeros.push(pagina);
        numero += 1;
    }
    const corpoNumerado = [];
    let contador = numeroInicialCorpo;
    for (const pagina of corpo) {
        pagina.meta.numeroPagina = contador;
        if (pagina.rodape)
            pagina.rodape.centro = config.numerarPaginas ? String(contador) : undefined;
        corpoNumerado.push(pagina);
        contador += 1;
    }
    const posNumerado = [];
    for (const pagina of posMateria) {
        pagina.meta.numeroPagina = contador;
        if (pagina.rodape)
            pagina.rodape.centro = config.numerarPaginas ? String(contador) : undefined;
        posNumerado.push(pagina);
        contador += 1;
    }
    const todas = [...comNumeros];
    if (incluirIndice) {
        const entradas = [
            ...preMateria
                .filter((p) => p.indice)
                .map((p) => ({ rotulo: p.indice, pagina: p.meta.numeroPagina ?? 0 })),
            ...corpoNumerado
                .filter((p) => p.indice)
                .map((p) => ({ rotulo: p.indice, pagina: p.meta.numeroPagina ?? 0 })),
            ...posNumerado
                .filter((p) => p.indice)
                .map((p) => ({ rotulo: p.indice, pagina: p.meta.numeroPagina ?? 0 })),
        ];
        const paginasDeIndice = construirIndice(ctx, entradas);
        paginasDeIndice.forEach((pagina, posicao) => {
            const numero = preMateria.length + 1 + posicao;
            pagina.meta.numeroPagina = numero;
            if (pagina.rodape) {
                pagina.rodape.centro = config.numerarPaginas ? String(numero) : undefined;
            }
        });
        todas.push(...paginasDeIndice);
    }
    todas.push(...corpoNumerado, ...posNumerado);
    /*
     * Camadas de imagem só podem ser distribuídas agora: o rodízio depende da
     * ordem final das páginas, e o índice foi inserido no meio do documento.
     */
    if (config.camadasImagem.length > 0 || config.fundos.length > 0) {
        const todasAsCamadas = [...camadasDosFundos(config), ...config.camadasImagem];
        const contador = new Map();
        todas.forEach((pagina, indice) => {
            pagina.camadas = camadasDaPagina(todasAsCamadas, {
                tipoPagina: pagina.tipo,
                indicePagina: indice,
                contador,
            });
        });
    }
    const paginasDeDiaGeradas = corpo.filter((pagina) => pagina.tipo === 'DIA').length;
    const resumo = {
        totalPaginas: todas.length,
        paginasDeDia: paginasDeDiaGeradas,
        formato: config.formato,
        formatoMm: `${ctx.medidas.larguraMm} × ${ctx.medidas.alturaMm} mm`,
        diasPorPagina: config.paginasDeDia === 'SEMANA' ? 7 : config.paginasDeDia === 'NENHUM' ? 0 : config.diasPorPagina,
        fimDeSemanaDividido: fimDeSemanaDividido(config),
        diasDaSemana: rotuloSemana(config.semana),
        periodo: config.calendario.modo === 'DATADO'
            ? D.rotuloPeriodo(config.calendario.inicio, config.calendario.fim)
            : `${paginasDeDiaGeradas} páginas de dia sem data`,
        datado: config.calendario.modo === 'DATADO',
        anosDaVisao: anosDaAgenda(config.calendario),
        estilo: rotuloEstilo(config.estilo),
        esquemaCores: ctx.paleta.esquemaAplicado, corPrincipal: ctx.paleta.principal,
        contrasteTexto: ctx.paleta.contrasteTexto,
        alturaEscritaMm: ctx.alturaEscritaMm,
        camadasImagem: config.camadasImagem.length,
        fundos: config.fundos.length,
        cadernos: config.cadernos.filter((caderno) => caderno.visivel).length,
        paginasDeCaderno: posNumerado.filter((pagina) => pagina.tipo === 'CADERNO').length,
        quadrosDoDia: quadrosLigados(config.quadros).map((quadro) => quadro),
    };
    return { paginas: todas, resumo, avisos: montarAvisos(ctx, totalDias) };
}
function assinaturaDoTema(ctx) {
    const tema = ctx.config.temaId;
    const encontrado = ASSINATURAS[tema];
    return encontrado ?? 'Um dia de cada vez.';
}
const ASSINATURAS = {
    aurora: 'Um dia de cada vez.',
    oceano: 'Respire. Siga o fluxo.',
    terracota: 'Feito à mão, no seu tempo.',
    floresta: 'Cresça um pouco por dia.',
    lavanda: 'Ideias também precisam de espaço.',
    grafite: 'Menos ruído, mais foco.',
    areia: 'Simples é suficiente.',
    vinho: 'Elegância está nos detalhes.',
    menta: 'Comece leve, termine forte.',
    'meia-noite': 'Planeje no silêncio.',
    'papel-antigo': 'Escrever é lembrar duas vezes.',
    'coral-vivo': 'Hoje conta.',
    PERSONALIZADO: 'Planejar é escolher com calma.',
};
function rotuloSemana(modo) {
    const mapa = {
        SEG_A_SEX: 'segunda a sexta',
        SEG_A_SAB: 'segunda a sábado',
        SEG_A_DOM: 'segunda a domingo',
        SAB_E_DOM: 'sábado e domingo',
    };
    return mapa[modo];
}
function rotuloEstilo(estilo) {
    /*
     * Sai de `ROTULO_ESTILO`, e não de uma lista escrita aqui dentro: a cópia local
     * ficou para trás quando o estilo de perguntas entrou, e o aviso do miolo saiu
     * sem rótulo. Uma lista só, no motor.
     */
    return ROTULO_ESTILO[estilo];
}
export function formatosSuportados() {
    return [...FORMATOS];
}
//# sourceMappingURL=construtor.js.map